/**
 * Histopathology Billing Table.
 * Migrated from admin4.0 HistoBillingTbl.js – same flow, design, layout and logic.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DescriptionIcon from "@mui/icons-material/Description";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import CommonValidation from "../../Utility/CommonValidation";
import { CurrencySymbol, TXN_MSG_MAX, TXN_MSG_MAX_LEN } from "../../Utility/Constants";
import { ImagePaths } from "../../Utility/ImagePaths";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import { getCachevalue } from "../../Utility/Services/CacheProviderService";
import { canEditTab } from "../../Utility/Services/RoleAccessService";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../Components/Loader";
import "../../Styles/Configuration.css";

const COMMON_STYLES = {
  HEIGHT: "2.5vw",
  FONT_SIZE_XS: "0.7vw",
  FONT_SIZE_SM: "0.75vw",
  FONT_SIZE_MD: "0.8vw",
  FONT_SIZE_L: "0.85vw",
  FONT_WEIGHT: 600,
};

const INPUT_SIZE_SX = {
  '& .MuiInputBase-root': { height: '2.25vw' },
  '& .MuiInputBase-input': { fontSize: '0.95vw', padding: '0.3vw 0.6vw' },
  '& .MuiSelect-select': { fontSize: '0.95vw', padding: '0.3vw 0.6vw' },
  '& .MuiInputLabel-root': { fontSize: '0.95vw', top: '-0.3vw', marginTop: '0.1vw' },
  '& .MuiInputLabel-shrink': { top: '0.05vw' },
  '& .MuiOutlinedInput-notchedOutline legend': { fontSize: '0.8vw' },
  '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' },
  '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { fontSize: '1.1vw' },
};

const DEFAULT_SERVICE_DATA = {
  rate_per_unit: "",
  unit: 1,
  unit_type: "Nos",
  total_amt: "",
  total_with_discount: "",
  doctor_amt: "",
  doctor_percent: "",
  old_doctor_amt: "",
  new_doctor_amt: "",
  hospital_amt: "",
  hospital_percent: "",
  old_hospital_amt: "",
  new_hospital_amt: "",
  service_name: "",
  service_head_name: "",
  service_code: "",
  service_head_code: "",
  edit_unit: false,
  edit_amount: false,
  edit_discount: false,
  doctor_name: "",
  doctor_id: "",
  doc_discount_amt: "",
  doc_discount_percent: "",
  hos_discount_amt: "",
  hos_discount_percent: "",
  concession_amt: "",
  concession_percent: "",
  gst_percent: 0,
  concession_comments: "",
  service_details_id: "",
  gst_amount: "",
  sgst_amount: "",
  cgst_amount: "",
  igst_amount: "",
  taxable_amount: "",
  sub_total_amount: "",
  concession_percent_w_r: "",
  doctor_percent_w_r: "",
  hospital_percent_w_r: "",
  module_type: "",
  is_hospital: false,
  service_head_id: null,
  op_order_number: "",
  id: null,
};


export default function HistoBillingTbl(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentTabTitle = location.pathname.includes("/ExistingBill")
    ? "Existing Bill"
    : (location.pathname.includes("/ModifyBill") ? "Overall Bills" : "New Bill");
  const canEdit = canEditTab(currentTabTitle);
  const {
    quickBillClear,
    patientId,
    getOrCreatePatientId,
    onBillSuccessClear,
    isExistingBill = false,
    isModifyBill = false,
    modifyBillId = null,
    searchResultSpecimen,
    nxtId,
    employerId = "",
    employeeId = "",
    corporateList = [],
    onEmployerIdChange,
    onEmployeeIdChange,
  } = props || {};
  const [modifyContext, setModifyContext] = useState({
    original_bill: null,
    original_line_ids: [],
    approved_line_ids: new Set(),
    warn_line_ids: new Set(),
    previous_bill_amount: 0,
    previous_received_amount: 0,
    // True once a SampleCollectionRecord exists for this bill. Drives the
    // lock on the Specimen Type dropdown while modifying the bill.
    is_sample_collected: false,
    hydrated: false,
  });
  const [deltaPopup, setDeltaPopup] = useState({ open: false, mode: null, form: {}, delta: 0, previous: 0, next: 0, reason: "" });
  const [isLoader, setIsLoader] = useState(false);
  const [openPopup, setOpenPopup] = useState(null);
  const [popupValues, setPopupValues] = useState({});
  const loggedInUserInfo = JSON.parse(getCachevalue("loggedInUserInfo") || "{}");
  const [loginInfo] = useState(loggedInUserInfo);
  // Tenant-level flag from Custom Configuration. When false, all concession-related
  // UI on the billing screen is hidden (per-line Concession column + input fields
  // AND the Sub Amount / Concession Applicable / Overall Concession / Taxable
  // Amount summary rows). DB / payload fields stay unchanged; this is purely visual.
  const needConcessionFlow = getCachevalue("need_concession_flow_in_billing") !== "false";
  const oneByOneCollectionFlow = getCachevalue("one_by_one_collection_flow") !== "false";
  const [duplicateCount, setDuplicateCount] = useState({});
  const [searchKey, setSearchKey] = useState("");
  const [message, setMessage] = useState({
    isErrorMsgText: "",
    successMsgText: "",
    isErrorMsg: false,
    successMsg: false,
  });
  const [billSuccessPopup, setBillSuccessPopup] = useState({ open: false, label: "" });
  const [billing, setBilling] = useState({
    billingList: [],
    dupBillList: "[]",
    SpecimenList: [],
    serviceSugg: [],
    allServiceConfigList: [],
    selected_specimen: null,
    selected_specimen_id: null,
    selected_service: null,
    selected_bill_doc: null,
    service_data: { ...DEFAULT_SERVICE_DATA },
    overall_data: {
      sub_total: "",
      round_off: "",
      payable_amt: "",
      taxable_amount: "",
      gst_amount: "",
      concession_applicable: "",
      actual_sub_total_amount: "",
    },
    isEdit: false,
    selectedIndex: null,
    selectedServiceIndex: null,
    selectedSubIndex: null,
    openDeletePopup: false,
    deleted_service: null,
    deletedIndex: null,
    cancelled_ids: [],
    overallDiscount: { discount_amount: "", discount_percentage: "", discount_percentage_w_r: "" },
  });
  const [requestState] = useState({
    fromApprover: false,
    fromBillConvert: false,
    modifyApprover: false,
  });
  const [paymentMode, setPaymentMode] = useState({
    isCash: false,
    isCard: false,
    isUPI: false,
    isBank: false,
    isCheque: false,
    cash_amt: "",
    card_amt: "",
    upi_amt: "",
    bank_amt: "",
    cheque_amt: "",
    card_no: "",
    bank_no: "",
    upi_no: "",
    cheque_no: "",
  });
  const [selectedCorporate, setSelectedCorporate] = useState(null);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [policyno, setPolicyno] = useState("");
  const [employeeNo, setEmployeeNo] = useState("");
  const [corporateData, setCorporateData] = useState([]);
  const [insuranceData, setInsuranceData] = useState([]);
  // Credit-bill flow switch — driven by Custom Configuration:
  //   "Add Credit Type Selection Popup in Billing"
  //     Yes (cache "true")  → open the popup and let the user choose
  //     No  (default)       → derive credit from refferals (no popup)
  const [useRefferalAsCredit, setUseRefferalAsCredit] = useState(
    getCachevalue("need_credit_type_option_popup_in_billing") !== "true"
  );
  const [creditPopupOpen, setCreditPopupOpen] = useState(false);
  const [popupCreditType, setPopupCreditType] = useState("patient");
  const [popupHospitalId, setPopupHospitalId] = useState("");
  const [popupHospitalName, setPopupHospitalName] = useState("");
  const [popupOpIpNo, setPopupOpIpNo] = useState("");
  // Synchronous mirrors of the typed referral inputs — react state can lag
  // behind a click-blur sequence, so we read these refs in validation.
  const doctorTypedRef = useRef("");
  const hospitalTypedRef = useRef("");
  const opIpTypedRef = useRef("");
  const [refferals, setRefferals] = useState({
    refferalList: [],
    hospitalList: [],
    type: "self",
    doctor: "",
    hospital: "",
    hospital_id: "",
    op_ip_no: "",
  });
  const [userList, setUserList] = useState([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState(null);

  const errorMessage = (msg) =>
    setMessage((prev) => ({ ...prev, isErrorMsg: true, isErrorMsgText: msg }));
  const successMessage = (msg) =>
    setMessage((prev) => ({ ...prev, successMsg: true, successMsgText: msg }));
  const msgClose = () =>
    setMessage((prev) => ({ ...prev, isErrorMsg: false, isErrorMsgText: "", successMsg: false, successMsgText: "" }));

  const getreferralDoc = useCallback(() => {
    RestAPIService.getAll(Serviceurls.HISTO_REFERRAL_DOCTOR)
      .then((res) => {
        if (res?.data?.status === "success")
          setRefferals((prev) => ({ ...prev, refferalList: res.data.data || [] }));
      })
      .catch((err) => errorMessage(err?.response?.data?.message || err?.message));
  }, []);

  const getHospitalList = useCallback(() => {
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_CORPORATE_CONFIG)
      .then((res) => {
        if (res?.data?.status === "success") {
          const items = (res.data.data || [])
            .map((c) => ({ id: c.id, name: (c.company_name || "").trim() }))
            .filter((c) => c.name);
          setRefferals((prev) => ({ ...prev, hospitalList: items }));
        }
      })
      .catch(() => {});
  }, []);

  const getSpecimenList = useCallback(() => {
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_SPECIMEN)
      .then((res) => {
        if (res?.data?.status === "success") {
          const active = (res.data.data || []).filter((item) => item.is_active);
          setBilling((prev) => ({ ...prev, SpecimenList: active }));
        }
      })
      .catch((err) => errorMessage(err?.response?.data?.message || err?.message));
  }, []);

  const getServiceConfigList = useCallback(() => {
    const params = new URLSearchParams({
      module_type: "HISTOPATHOLOGY",
      service_head_id: "",
      service_details_id: "",
      service_head: "",
      service_name: "",
      service_head_code: "",
      charge_type: "Normal",
    });
    RestAPIService.getAll(Serviceurls.NEW_DEPARTMENT_MASTER + "?" + params.toString())
      .then((res) => {
        if (res?.data?.status === "success") {
          const raw = res.data.data || [];
          const mapped = raw.map((row) => {
            const headDetails = row.service_head_details ?? "";
            const serviceHead = headDetails.includes(" - ") ? headDetails.split(" - ").slice(1).join(" - ").trim() : headDetails;
            return {
              ...row,
              service_head: row.service_head ?? serviceHead,
              charge_id: row.service_charge_id,
              doctor_share_amount: row.doctor_share_amount ?? 0,
              hospital_share_amount: row.hospital_share_amount ?? 0,
              doctor_share_percent: row.doctor_share_percent ?? 0,
              hospital_share_percent: row.hospital_share_percent ?? 0,
            };
          });
          setBilling((prev) => ({ ...prev, allServiceConfigList: mapped }));
        }
      })
      .catch((err) => errorMessage(err?.response?.data?.message || err?.message));
  }, []);

  useEffect(() => {
    getreferralDoc();
    getSpecimenList();
    getServiceConfigList();
    getHospitalList();
    // Load user list for the "Billed By" dropdown. Leave the selection blank —
    // biller must explicitly pick a name so the audit trail is intentional,
    // not just whoever happens to be logged in.
    RestAPIService.getAll(`${Serviceurls.HISTOPATHOLOGY_USER_ACCESS_ROLE_MAPPING}?include_admin=1`)
      .then((res) => {
        if (res?.data?.status === "success") {
          setUserList(res.data.data || []);
        }
      })
      .catch(() => {});
  }, [getreferralDoc, getSpecimenList, getServiceConfigList, getHospitalList]);

  const billingCalculation = useCallback(() => {
    const list = billing.billingList || [];
    if (list.length === 0) {
      setBilling((prev) => ({
        ...prev,
        overall_data: {
          ...prev.overall_data,
          sub_total: 0,
          round_off: 0,
          payable_amt: 0,
          taxable_amount: 0,
          gst_amount: 0,
          concession_applicable: 0,
          actual_sub_total_amount: 0,
        },
      }));
      return;
    }
    const calData = list.reduce(
      (acc, el) => {
        acc.sub_total += +(el.total_with_discount ?? 0);
        acc.actual_sub_total_amount += +(el.sub_total_amount ?? 0);
        acc.taxable_amount += +(el.taxable_amount ?? 0);
        acc.gst_amount += +(el.gst_amount ?? 0);
        acc.concession_applicable += +(el.concession_applicable ?? 0);
        return acc;
      },
      { sub_total: 0, taxable_amount: 0, gst_amount: 0, concession_applicable: 0, actual_sub_total_amount: 0 }
    );
    calData.sub_total = +CommonValidation.formatAmount(calData.sub_total);
    calData.taxable_amount = +CommonValidation.formatAmount(calData.taxable_amount);
    calData.gst_amount = +CommonValidation.formatAmount(calData.gst_amount);
    calData.concession_applicable = +CommonValidation.formatAmount(calData.concession_applicable);
    calData.actual_sub_total_amount = +CommonValidation.formatAmount(calData.actual_sub_total_amount);
    const discountAmt = +(billing.overallDiscount?.discount_amount ?? 0);
    const finalPay = calData.taxable_amount + calData.gst_amount - discountAmt;
    calData.round_off = CommonValidation.formatAmount(Math.round(finalPay) - finalPay);
    calData.payable_amt = Math.round(finalPay);
    setBilling((prev) => ({ ...prev, overall_data: { ...prev.overall_data, ...calData } }));
  }, [billing.billingList, billing.overallDiscount?.discount_amount]);

  useEffect(() => {
    if (billing.billingList?.length > 0) {
      billingCalculation();
    } else if (billing.billingList?.length === 0) {
      setBilling((prev) => ({
        ...prev,
        overall_data: {
          ...prev.overall_data,
          sub_total: 0,
          round_off: 0,
          payable_amt: 0,
          taxable_amount: 0,
          gst_amount: 0,
          concession_applicable: 0,
        },
      }));
    }
  }, [billing.billingList, billing.overallDiscount?.discount_amount, billing.overallDiscount?.discount_percentage_w_r, billingCalculation]);

  // Clear overall discount when concession_applicable becomes 0 (avoids stale NaN displays).
  // Guard: fire ONLY when a real calculation has produced 0 — the initial state
  // is "" (empty string), which also coerces to 0 and would otherwise wipe the
  // values just hydrated from an existing/modify bill before billingCalculation
  // has a chance to compute concession_applicable.
  useEffect(() => {
    const ca = billing.overall_data?.concession_applicable;
    const caIsConcreteZero = ca !== "" && ca !== null && ca !== undefined && +ca === 0;
    if (caIsConcreteZero &&
        (billing.overallDiscount?.discount_amount || billing.overallDiscount?.discount_percentage_w_r)) {
      setBilling((prev) => ({
        ...prev,
        overallDiscount: { discount_amount: "", discount_percentage: "", discount_percentage_w_r: "" },
      }));
    }
  }, [billing.overall_data?.concession_applicable, billing.overallDiscount?.discount_amount, billing.overallDiscount?.discount_percentage_w_r]);

  useEffect(() => {
    if (billing.billingList?.length === 0 && !isExistingBill && !isModifyBill) {
      setBilling((prev) => ({ ...prev, selected_specimen: null, selected_specimen_id: null }));
    }
  }, [billing.billingList?.length, isExistingBill, isModifyBill]);

  // Modify mode fallback: when the bill has no SampleCollectionRecord yet, the
  // by-nxt-id endpoint returns null specimen. Derive specimen from the first
  // line's ServiceDetails master (allServiceConfigList) so the dropdown fills.
  useEffect(() => {
    if (!isModifyBill) return;
    if (billing.selected_specimen_id) return;
    if (!(billing.billingList?.length > 0)) return;
    if (!(billing.allServiceConfigList?.length > 0)) return;
    if (!(billing.SpecimenList?.length > 0)) return;
    const firstLine = billing.billingList[0];
    const svc = billing.allServiceConfigList.find(
      (s) => +s.service_details_id === +firstLine.service_details_id
    );
    const specId = svc?.specimen_id;
    if (!specId) return;
    const spec = billing.SpecimenList.find((s) => s.id === specId);
    if (!spec) return;
    setBilling((prev) => ({
      ...prev,
      selected_specimen: spec,
      selected_specimen_id: specId,
      billingList: prev.billingList.map((it, idx) => (idx === 0 ? { ...it, specimen_id: specId } : it)),
    }));
  }, [
    isModifyBill,
    billing.selected_specimen_id,
    billing.billingList,
    billing.allServiceConfigList,
    billing.SpecimenList,
  ]);

  useEffect(() => {
    if (!searchResultSpecimen?.specimen_id) return;
    const specimenOption = {
      id: searchResultSpecimen.specimen_id,
      specimen_type: searchResultSpecimen.specimen_type ?? "",
      specimen_code: searchResultSpecimen.specimen_code ?? "",
    };
    setBilling((prev) => {
      const list = prev.SpecimenList || [];
      const exists = list.some((s) => s.id === searchResultSpecimen.specimen_id);
      const nextList = exists ? list : [specimenOption, ...list];
      return {
        ...prev,
        SpecimenList: nextList,
        selected_specimen: specimenOption,
        selected_specimen_id: searchResultSpecimen.specimen_id,
      };
    });
  }, [searchResultSpecimen?.specimen_id]);

  const effectiveSpecimenId = billing.selected_specimen_id ?? billing.billingList?.[0]?.specimen_id ?? null;
  const filteredServiceSugg = useMemo(() => {
    const list = billing.allServiceConfigList || [];
    if (!list.length) return [];
    const activeSpecimenIds = new Set((billing.SpecimenList || []).map((s) => s.id));
    const onlyActive = list.filter((s) => activeSpecimenIds.has(s.specimen_id));
    if (effectiveSpecimenId != null) return onlyActive.filter((s) => s.specimen_id === effectiveSpecimenId);
    return onlyActive;
  }, [billing.allServiceConfigList, billing.SpecimenList, effectiveSpecimenId]);

  const initialOverallData = {
    sub_total: "",
    round_off: "",
    payable_amt: "",
    taxable_amount: "",
    gst_amount: "",
    net_amount: "",
    concession_applicable: "",
    actual_sub_total_amount: "",
  };
  const initialPaymentMode = {
    isCash: false,
    isCard: false,
    isUPI: false,
    isBank: false,
    isCheque: false,
    cash_amt: "",
    card_amt: "",
    upi_amt: "",
    bank_amt: "",
    cheque_amt: "",
    card_no: "",
    bank_no: "",
    upi_no: "",
    cheque_no: "",
  };

  useEffect(() => {
    if (quickBillClear) {
      setBilling((prev) => ({
        ...prev,
        billingList: [],
        dupBillList: "[]",
        cancelled_ids: [],
        selected_service: null,
        selected_specimen: null,
        selected_specimen_id: null,
        service_data: { ...DEFAULT_SERVICE_DATA },
        isEdit: false,
        selectedIndex: null,
        overall_data: initialOverallData,
        overallDiscount: { discount_amount: "", discount_percentage: "", discount_percentage_w_r: "" },
      }));
      setPaymentMode(initialPaymentMode);
      setRefferals((prev) => ({ ...prev, type: "self", doctor: "", hospital: "", hospital_id: "", op_ip_no: "" }));
    }
  }, [quickBillClear]);

  const onOpenPopupHandler = (type) => {
    setOpenPopup(type);
    setPopupValues({
      ...billing.service_data,
      concession_amt: billing.service_data?.concession_amt,
      concession_percent: billing.service_data?.concession_percent,
      concession_comments: billing.service_data?.concession_comments,
    });
  };

  const onClickUnitHandler = useCallback(
    (type) => {
      setBilling((prev) => {
        const sd = { ...prev.service_data };
        if (type === "increase") sd.unit = (sd.unit || 1) + 1;
        else if (type === "decrease") sd.unit = Math.max(1, (sd.unit || 1) - 1);
        const baseTotal = sd.unit * (sd.rate_per_unit || 0);
        if (sd.doctor_amt || sd.hospital_amt) {
          const oldDoc = CommonValidation.getDiscountAmountWithRound(sd.doctor_percent, baseTotal);
          const oldHos = CommonValidation.getDiscountAmountWithRound(sd.hospital_percent, baseTotal);
          const disDoc = CommonValidation.getDiscountAmountWithRound(sd.doc_discount_percent, oldDoc);
          const disHos = CommonValidation.getDiscountAmountWithRound(sd.hos_discount_percent, oldHos);
          const conTlt = +disDoc + +disHos;
          sd.total_amt = baseTotal;
          sd.old_doctor_amt = oldDoc;
          sd.old_hospital_amt = oldHos;
          sd.new_doctor_amt = oldDoc - disDoc;
          sd.new_hospital_amt = oldHos - disHos;
          sd.doctor_amt = oldDoc - disDoc;
          sd.hospital_amt = oldHos - disHos;
          sd.doc_discount_amt = disDoc;
          sd.hos_discount_amt = disHos;
          sd.concession_amt = CommonValidation.formatAmount(conTlt);
          sd.concession_percent = CommonValidation.getDiscountPercentage(conTlt, baseTotal);
          sd.concession_percent_w_r = CommonValidation.getDiscountPercentageWithRound(conTlt, baseTotal);
        } else {
          sd.total_amt = baseTotal;
          sd.concession_amt = CommonValidation.getDiscountAmountWithRound(sd.concession_percent, baseTotal);
        }
        return { ...prev, service_data: sd };
      });
    },
    [billing.service_data]
  );

  const withOutShareHandler = useCallback(
    (e, key) => {
      const value = e.target.value;
      setBilling((prev) => {
        const update = { ...prev.service_data };
        if (key === "rate_per_unit") {
          if (CommonValidation.DecimalNumber(value) || value === "") {
            update.rate_per_unit = value;
            update.total_amt = (update.unit || 1) * (value ? +value : 0);
            update.concession_amt = value ? CommonValidation.getDiscountAmountWithRound(update.concession_percent, update.total_amt) : "";
          }
        } else if (key === "concession_discount_amt") {
          const totalAmt = +(update.total_amt ?? 0);
          if ((CommonValidation.DecimalNumber(value) && +value <= totalAmt) || value === "") {
            update.concession_amt = value;
            update.concession_percent = value ? CommonValidation.getDiscountPercentage(value, update.total_amt) : "";
            update.concession_percent_w_r = value ? CommonValidation.getDiscountPercentageWithRound(value, update.total_amt) : "";
          }
        } else if (key === "concession_discount_percent") {
          if ((CommonValidation.DecimalNumber(value) && value <= 100) || value === "") {
            update.concession_percent = value;
            update.concession_percent_w_r = value;
            update.concession_amt = CommonValidation.getDiscountAmountWithRound(value, update.total_amt);
          }
        }
        return { ...prev, service_data: update };
      });
    },
    [billing.service_data]
  );

  const onEditHandler = (item, index = null, subIndex = null, mainIndex = null) => {
    const fallbackSpecId = item?.specimen_id || billing.selected_specimen_id;
    const specimen = fallbackSpecId && billing.SpecimenList?.length
      ? billing.SpecimenList.find((s) => s.id === fallbackSpecId) ?? null
      : null;
    setBilling((prev) => ({
      ...prev,
      selected_service: item,
      selected_bill_doc: item?.doctor_name,
      service_data: { ...item, specimen_id: item?.specimen_id ?? prev.selected_specimen_id ?? null },
      isEdit: true,
      selectedIndex: index,
      selectedSubIndex: subIndex ?? null,
      selectedServiceIndex: mainIndex ?? null,
      selected_specimen: specimen ?? prev.selected_specimen,
      selected_specimen_id: fallbackSpecId ?? prev.selected_specimen_id,
    }));
  };

  const onSelectServiceHandler = (value) => {
    if (!value) return;
    const serviceData = "emergency" in value ? value.emergency : "holiday" in value ? value.holiday : value;
    const sharedFields = {
      rate_per_unit: serviceData.service_charge,
      unit: serviceData.unit ?? 1,
      unit_type: serviceData.unit_type || "Nos",
      total_amt: serviceData.service_charge,
      total_with_discount: serviceData.service_charge,
      doctor_amt: serviceData.doctor_share_amount || "",
      doctor_percent: serviceData.doctor_share_percent || "",
      old_doctor_amt: serviceData.doctor_share_amount || "",
      new_doctor_amt: serviceData.doctor_share_amount || "",
      hospital_amt: serviceData.hospital_share_amount || "",
      hospital_percent: serviceData.hospital_share_percent || "",
      old_hospital_amt: serviceData.hospital_share_amount || "",
      new_hospital_amt: serviceData.hospital_share_amount || "",
      service_name: serviceData.service_name,
      service_head_name: serviceData.service_head,
      service_code: serviceData.service_code,
      service_head_code: serviceData.service_head_code,
      edit_unit: serviceData.edit_unit ?? true,
      edit_amount: serviceData.edit_amount ?? true,
      edit_discount: serviceData.edit_discount ?? true,
      doctor_name: serviceData.doctor_name || "",
      doctor_id: serviceData.doctor_id || "",
      gst_percent: serviceData.gst_percent || 0,
      service_details_id: serviceData.service_details_id,
      charge_id: serviceData.charge_id,
      review_days: serviceData.review_days,
      review_count: serviceData.review_count,
      module_type: serviceData.module_type,
      doctor_percent_w_r: +(serviceData.doctor_share_percent?.toFixed(2) || 0),
      hospital_percent_w_r: +(serviceData.hospital_share_percent?.toFixed(2) || 0),
      start_date: new Date(),
      end_date: null,
      is_hospital: serviceData.is_hospital || false,
      service_head_id: serviceData.service_head_id,
      op_order_number: serviceData.op_order_number || "",
      doc_discount_amt: "",
      doc_discount_percent: "",
      hos_discount_amt: "",
      hos_discount_percent: "",
      concession_amt: "",
      concession_percent: "",
      concession_percent_w_r: "",
      specimen_id: serviceData.specimen_id ?? null,
    };
    if (serviceData.membership_discount_percentage) {
      sharedFields.concession_percent = serviceData.membership_discount_percentage;
      sharedFields.concession_percent_w_r = serviceData.membership_discount_percentage;
      sharedFields.concession_amt = CommonValidation.getDiscountAmountWithRound(
        serviceData.membership_discount_percentage,
        sharedFields.total_amt
      );
      sharedFields.total_with_discount = sharedFields.total_amt - sharedFields.concession_amt;
    }
    setBilling((prev) => {
      const next = {
        ...prev,
        selected_service: value,
        service_data: { ...prev.service_data, ...sharedFields },
      };
      if (!prev.selected_specimen_id && serviceData.specimen_id && prev.SpecimenList?.length) {
        const specimen = prev.SpecimenList.find((s) => s.id === serviceData.specimen_id);
        if (specimen) {
          next.selected_specimen = specimen;
          next.selected_specimen_id = specimen.id;
        }
      }
      return next;
    });
  };

  const clearService = () => {
    setBilling((prev) => ({
      ...prev,
      selected_service: null,
      service_data: { ...DEFAULT_SERVICE_DATA },
    }));
  };

  const onClearServiceData = () => {
    setBilling((prev) => {
      const next = {
        ...prev,
        selected_service: null,
        service_data: { ...DEFAULT_SERVICE_DATA },
        isEdit: false,
        selectedIndex: null,
        selectedServiceIndex: null,
        selectedSubIndex: null,
      };
      if (!isExistingBill) {
        next.selected_specimen = null;
        next.selected_specimen_id = null;
      }
      return next;
    });
  };

  const checkDuplicate = () => {
    for (let i = 0; i < billing.billingList.length; i++) {
      if (i !== billing.selectedIndex) {
        const el = billing.billingList[i];
        if (
          el?.service_head_code === billing.service_data?.service_head_code &&
          el?.service_code === billing.service_data?.service_code &&
          el?.service_details_id === billing.service_data?.service_details_id
        )
          return "Service Already Exist";
      }
    }
    return null;
  };

  const onAddHandler = () => {
    if (!billing.selected_specimen && !billing.selected_specimen_id) {
      return errorMessage("Please select a Specimen Type");
    }
    if (!billing.selected_service && !billing.service_data?.service_name) {
      return errorMessage("Please select a Service");
    }
    // Resolve each line's specimen from the service master (Modify Bill lines only
    // carry specimen_id on the first row) and block mixing specimens in one bill.
    const specimenOf = (item) =>
      item?.specimen_id ??
      (billing.allServiceConfigList || []).find((s) => +s.service_details_id === +item?.service_details_id)?.specimen_id ??
      null;
    const newSpecimenId = billing.selected_specimen_id ?? specimenOf(billing.service_data);
    const mixed = (billing.billingList || []).some((it, i) => {
      if (i === billing.selectedIndex) return false;
      const sid = specimenOf(it);
      return sid != null && newSpecimenId != null && +sid !== +newSpecimenId;
    });
    if (mixed) {
      return errorMessage("A bill can contain services of only one specimen type");
    }
    const discountTotal = +billing.service_data.total_amt - +(billing.service_data.concession_amt || 0);
    const isGst = loginInfo?.is_gst_enabled;
    const incGst = isGst ? CommonValidation.calculateTotalWithGST(discountTotal, billing.service_data.gst_percent || 0) : 0;
    const tltGstDiff = isGst ? incGst - discountTotal : 0;
    const sgstCgst = isGst ? tltGstDiff / 2 : 0;
    const service_list = {
      ...billing.service_data,
      specimen_id: billing.selected_specimen_id ?? billing.service_data?.specimen_id ?? null,
      total_with_discount: discountTotal,
      service_with_gst_total_amt: isGst ? CommonValidation.formatAmount(incGst) : discountTotal,
      gst_amount: CommonValidation.formatAmount(tltGstDiff),
      sgst_amount: CommonValidation.formatAmount(sgstCgst),
      cgst_amount: CommonValidation.formatAmount(sgstCgst),
      igst_amount: CommonValidation.formatAmount(tltGstDiff),
      taxable_amount: discountTotal,
      sub_total_amount: billing.service_data.total_amt,
      concession_applicable: billing.service_data.edit_discount
        ? billing.service_data.is_hospital
          ? billing.service_data.new_hospital_amt
          : discountTotal
        : 0,
      module_type: billing.service_data.module_type,
      unit: billing.service_data.unit,
      service_head_id: billing.service_data.service_head_id,
      doctor_id: billing.selected_bill_doc?.doctor_id || billing.service_data.doctor_id,
      doctor_name: billing.selected_bill_doc?.doctor_name || billing.service_data.doctor_name,
      id: billing.service_data?.id ?? null,
      not_from_order: true,
    };
    const err = checkDuplicate();
    if (err) return errorMessage(err);
    setBilling((prev) => {
      const list = [...prev.billingList];
      if (prev.isEdit && prev.selectedIndex != null) {
        list[prev.selectedIndex] = service_list;
      } else {
        list.push(service_list);
      }
      return {
        ...prev,
        billingList: list,
        dupBillList: JSON.stringify(list),
        overallDiscount: { discount_amount: "", discount_percentage: "", discount_percentage_w_r: "" },
      };
    });
    setTimeout(onClearServiceData, 50);
  };

  const handleCheckBox = (e, key) => {
    const checked = e.target.checked;
    setPaymentMode((prev) => ({
      ...prev,
      [key]: checked,
      cash_amt: key === "isCash" && !checked ? "" : prev.cash_amt,
      card_amt: key === "isCard" && !checked ? "" : prev.card_amt,
      upi_amt: key === "isUPI" && !checked ? "" : prev.upi_amt,
      bank_amt: key === "isBank" && !checked ? "" : prev.bank_amt,
      cheque_amt: key === "isCheque" && !checked ? "" : prev.cheque_amt,
      card_no: key === "isCard" && !checked ? "" : prev.card_no,
      bank_no: key === "isBank" && !checked ? "" : prev.bank_no,
      upi_no: key === "isUPI" && !checked ? "" : prev.upi_no,
      cheque_no: key === "isCheque" && !checked ? "" : prev.cheque_no,
    }));
  };

  const payableAmt = +(billing.overall_data?.payable_amt ?? 0);

  useEffect(() => {
    if (getCachevalue("auto_fetch_bill_amount_to_payment") !== "true") return;
    if (!payableAmt) return;
    setPaymentMode((prev) => {
      const { isCash, isCard, isUPI, isBank, isCheque } = prev;
      const count = [isCash, isCard, isUPI, isBank, isCheque].filter(Boolean).length;
      if (count !== 1) return prev;
      return {
        ...prev,
        cash_amt: isCash ? String(payableAmt) : prev.cash_amt,
        card_amt: isCard ? String(payableAmt) : prev.card_amt,
        upi_amt: isUPI ? String(payableAmt) : prev.upi_amt,
        bank_amt: isBank ? String(payableAmt) : prev.bank_amt,
        cheque_amt: isCheque ? String(payableAmt) : prev.cheque_amt,
      };
    });
  }, [
    payableAmt,
    paymentMode.isCash,
    paymentMode.isCard,
    paymentMode.isUPI,
    paymentMode.isBank,
    paymentMode.isCheque,
  ]);

  useEffect(() => {
    if (!isModifyBill || !modifyBillId || modifyContext.hydrated) return;
    RestAPIService.getAll(`${Serviceurls.HISTOPATHOLOGY_BILLING_BILL}?id=${modifyBillId}`)
      .then((res) => {
        if (res?.data?.status !== "success") return;
        const bill = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
        if (!bill) return;
        const items = (bill.line_items || []).map((li) => {
          const parts = String(li.service_description || "").split(" - ");
          const svcName = parts[0] || li.service_description || "";
          const headName = parts.slice(1).join(" - ") || "";
          return {
            service_details_id: li.service_details_id,
            service_name: svcName,
            service_head_name: headName,
            service_description: li.service_description || "",
            unit: +(li.units || 1),
            rate_per_unit: +(li.rate_per_unit || 0),
            total_amt: +(li.sub_total || 0),
            sub_total_amount: +(li.sub_total || 0),
            concession_amt: +(li.discount_amount || 0),
            concession_percent_w_r: li.discount_percentage || "0",
            total_with_discount: +(li.net_amount || 0),
            taxable_amount: +(li.net_amount || 0),
            gst_amount: 0,
            sgst_amount: 0,
            cgst_amount: 0,
            igst_amount: 0,
            concession_applicable: +(li.net_amount || 0),
            service_with_gst_total_amt: +(li.net_amount || 0),
            edit_unit: true,
            edit_amount: true,
            edit_discount: true,
            bill_line_item_id: li.id,
          };
        });
        setBilling((prev) => ({
          ...prev,
          billingList: items,
          dupBillList: JSON.stringify(items),
          overall_data: {
            ...prev.overall_data,
            sub_total: bill.sub_total || "",
            taxable_amount: bill.taxable_amount || "",
            round_off: bill.round_off || "",
            payable_amt: bill.payable_amount || "",
            gst_amount: "",
            concession_applicable: "",
            actual_sub_total_amount: "",
          },
          overallDiscount: {
            discount_amount: bill.overall_concession_amount || "",
            discount_percentage: bill.overall_concession_percent || "",
            discount_percentage_w_r: bill.overall_concession_percent || "",
          },
        }));
        setModifyContext({
          original_bill: bill,
          original_line_ids: items.map((it) => it.bill_line_item_id).filter(Boolean),
          approved_line_ids: new Set(),
          warn_line_ids: new Set(),
          previous_bill_amount: +(bill.payable_amount || 0),
          previous_received_amount: +(bill.received_amount || 0),
          is_sample_collected: !!bill.is_sample_collected,
          is_existing_bill: !!bill.is_existing_bill,
          hydrated: true,
        });
        if (bill.nxt_id) {
          RestAPIService.getAll(`${Serviceurls.HISTOPATHOLOGY_REPORTS_PATIENT_WISE}?nxt_id=${encodeURIComponent(bill.nxt_id)}`)
            .then((r2) => {
              if (r2?.data?.status !== "success") return;
              const services = r2.data.data?.services || [];
              const approved = new Set(), warn = new Set();
              services.forEach((s) => {
                if (s.bill_id !== bill.id || !s.bill_line_item_id) return;
                if (s.result_status === "approved") approved.add(s.bill_line_item_id);
                else if (["draft", "saved", "sent_for_approval"].includes(s.result_status)) warn.add(s.bill_line_item_id);
              });
              setModifyContext((prev) => ({ ...prev, approved_line_ids: approved, warn_line_ids: warn }));
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [isModifyBill, modifyBillId, modifyContext.hydrated]);

  const onPaymentChange = (key, value) => {
    if (key === "cash_amt" || key === "card_amt" || key === "upi_amt" || key === "bank_amt" || key === "cheque_amt") {
      if (value !== "" && !CommonValidation.DecimalNumber(value)) return;
      if (value !== "" && +value > payableAmt) return;
    }
    setPaymentMode((prev) => ({ ...prev, [key]: value }));
  };

  const payamt = (+(paymentMode.cash_amt || 0)) + (+(paymentMode.card_amt || 0)) + (+(paymentMode.upi_amt || 0)) + (+(paymentMode.bank_amt || 0)) + (+(paymentMode.cheque_amt || 0));

  const buildBillPayload = useCallback(
    (status = "draft", overridePatientId = undefined) => {
      const effectivePatientId = overridePatientId !== undefined ? overridePatientId : patientId;
      const list = billing.billingList || [];
      const referralType = refferals?.type === "doctor" ? "doctor" : "self";
      const overallDiscountAmt = +(billing.overallDiscount?.discount_amount ?? 0);
      const overallDiscountPct = billing.overallDiscount?.discount_percentage_w_r ?? "0";
      const totalNetBeforeBillDiscount = list.reduce(
        (sum, item) => sum + +(item.total_with_discount ?? 0),
        0
      );
      const line_items = list.map((item) => {
        const subTotal = +(item.sub_total_amount ?? item.total_amt ?? 0) || (+(item.unit ?? 1) * +(item.rate_per_unit ?? 0));
        const concessionAmt = +(item.concession_amt ?? 0);
        const netAmt = +(item.total_with_discount ?? 0);
        const discountPct = subTotal > 0 ? CommonValidation.formatAmount((concessionAmt / subTotal) * 100) : "0";
        const lineShareOfBillDiscount =
          totalNetBeforeBillDiscount > 0 && overallDiscountAmt > 0
            ? (netAmt / totalNetBeforeBillDiscount) * overallDiscountAmt
            : 0;
        const billDiscountAmount = +CommonValidation.formatAmount(lineShareOfBillDiscount);
        const billNetAmount = netAmt - billDiscountAmount;
        const billDiscountPct =
          netAmt > 0 && billDiscountAmount !== 0
            ? CommonValidation.formatAmount((billDiscountAmount / netAmt) * 100)
            : "0";
        const serviceDesc = [item.service_name, item.service_code].filter(Boolean).length
          ? `${item.service_name || ""}${item.service_code ? ` (${item.service_code})` : ""}`
          : "";
        const headPart = [item.service_head_name, item.service_head_code].filter(Boolean).length
          ? `${item.service_head_name || ""}${item.service_head_code ? ` (${item.service_head_code})` : ""}`
          : "";
        const service_description = [serviceDesc, headPart].filter(Boolean).join(" - ") || "Service";

        return {
          service_details_id: +(item.service_details_id ?? 0),
          service_description,
          units: +(item.unit ?? 1),
          rate_per_unit: CommonValidation.formatAmount(item.rate_per_unit ?? 0),
          sub_total: CommonValidation.formatAmount(subTotal),
          discount_percentage: discountPct,
          discount_amount: CommonValidation.formatAmount(concessionAmt),
          net_amount: CommonValidation.formatAmount(netAmt),
          bill_discount_percentage: String(billDiscountPct),
          bill_discount_amount: CommonValidation.formatAmount(billDiscountAmount),
          bill_net_amount: CommonValidation.formatAmount(billNetAmount),
          concession: CommonValidation.formatAmount(concessionAmt),
          amount: CommonValidation.formatAmount(billNetAmount),
        };
      });
      const taxableAmount = +(billing.overall_data?.taxable_amount ?? 0) + +(billing.overall_data?.gst_amount ?? 0) - overallDiscountAmt;
      const roundOff = +(billing.overall_data?.round_off ?? 0);
      const payableAmount = +(billing.overall_data?.payable_amt ?? 0);
      const receivedAmount = +(paymentMode.cash_amt || 0) + +(paymentMode.card_amt || 0) + +(paymentMode.upi_amt || 0) + +(paymentMode.bank_amt || 0) + +(paymentMode.cheque_amt || 0)

      const payload = {
        patient_id: +(effectivePatientId ?? 0),
        referral_type: referralType,
        referred_doctor_name: referralType === "self"
          ? ""
          : (doctorTypedRef.current
              || (typeof refferals?.doctor === "string"
                  ? refferals.doctor
                  : (refferals?.doctor?.name || ""))
            ).trim(),
        referred_hospital_name: referralType === "self"
          ? ""
          : (hospitalTypedRef.current || refferals?.hospital || "").trim(),
        op_ip_number: (opIpTypedRef.current || refferals?.op_ip_no || "").trim(),
        status: (CommonValidation.formatAmount(payableAmount) === 0 || (CommonValidation.formatAmount(payableAmount) === CommonValidation.formatAmount(receivedAmount))) ? "paid" : CommonValidation.formatAmount(receivedAmount) === 0 ? "not paid" : "partial",
        line_items,
        overall_concession_amount: CommonValidation.formatAmount(overallDiscountAmt),
        overall_concession_percent: String(overallDiscountPct),
        taxable_amount: CommonValidation.formatAmount(taxableAmount),
        round_off: CommonValidation.formatAmount(roundOff),
        payable_amount: CommonValidation.formatAmount(payableAmount),
        received_amount: CommonValidation.formatAmount( receivedAmount),
        cash_amount: CommonValidation.formatAmount((paymentMode.cash_amt || 0)),
        upi_amount: CommonValidation.formatAmount((paymentMode.upi_amt || 0)),
        card_amount: CommonValidation.formatAmount((paymentMode.card_amt || 0)),
        bank_amount: CommonValidation.formatAmount((paymentMode.bank_amt || 0)),
        cheque_amount: CommonValidation.formatAmount((paymentMode.cheque_amt || 0)),
        card_transaction_no:  String(paymentMode.card_no || "").trim(),
        upi_transaction_no:  String(paymentMode.upi_no || "").trim(),
        bank_transaction_no:  String(paymentMode.bank_no || "").trim(),
        cheque_transaction_no:  String(paymentMode.cheque_no || "").trim(),
        is_existing_bill: !!isExistingBill,
      };
      if (selectedCreatedBy?.username) {
        payload.created_by = selectedCreatedBy.username;
      }
      if (nxtId != null && nxtId !== "") {
        payload.nxt_id = String(nxtId);
      }
      if (status === "credit" || status === "not paid" || status === "partial") {
        let isCorporate;
        let corporate_id = null;
        let corporate_name = "";
        let employee_id_no = "";
        if (useRefferalAsCredit) {
          isCorporate = referralType === "doctor";
          corporate_id = refferals?.hospital_id !== "" && refferals?.hospital_id != null
            ? Number(refferals?.hospital_id)
            : null;
          corporate_name = String(refferals?.hospital || "").trim();
          employee_id_no = String(refferals?.op_ip_no || "").trim();
        } else {
          isCorporate = popupCreditType === "corporate";
          corporate_id = popupHospitalId !== "" && popupHospitalId != null
            ? Number(popupHospitalId)
            : null;
          corporate_name = String(popupHospitalName || "").trim();
          employee_id_no = String(popupOpIpNo || "").trim();
        }
        payload.credit_type = isCorporate ? "corporate" : "patient";
        if (isCorporate && corporate_id != null) {
          payload.corporate_id = corporate_id;
        }
        payload.corporate_name = isCorporate ? corporate_name : "";
        payload.employee_id_no = isCorporate ? employee_id_no : "";
      }
      return payload;
    },
    [billing.billingList, billing.overall_data, billing.overallDiscount, refferals, patientId, isExistingBill, paymentMode, nxtId, useRefferalAsCredit, popupCreditType, popupHospitalId, popupHospitalName, popupOpIpNo, selectedCreatedBy]
  );

  const openModifyDelta = useCallback(() => {
    const newPayable = +(billing.overall_data?.payable_amt ?? 0);
    const prevPayable = modifyContext.previous_bill_amount;
    const prevReceived = modifyContext.previous_received_amount;
    const delta = newPayable - prevPayable;
    // If the original bill was NOT fully paid (a credit / partial credit bill),
    // the modify flow must not force an extra payment — the delta just changes
    // the outstanding balance. Extra payment is only meaningful when the bill
    // was fully cleared and the new amount pushes above what was received.
    const wasFullyPaid = prevPayable > 0 && prevReceived >= prevPayable;
    const extraNeeded = wasFullyPaid ? Math.max(0, newPayable - prevReceived) : 0;
    // Refund still applies whenever the customer's paid amount exceeds the new
    // payable — independent of credit-vs-paid classification.
    const refundNeeded = Math.max(0, prevReceived - newPayable);
    const mode = extraNeeded > 0 ? "extra" : (refundNeeded > 0 ? "refund" : "noop");
    setDeltaPopup({
      open: true,
      mode,
      previous: prevPayable,
      next: newPayable,
      delta,
      extraNeeded,
      refundNeeded,
      form: {
        cash_amount: "", card_amount: "", upi_amount: "", bank_amount: "", cheque_amount: "",
        card_transaction_no: "", upi_transaction_no: "", bank_transaction_no: "", cheque_transaction_no: "",
      },
      // When true, the additional amount required is left uncollected — the bill
      // just carries it as outstanding/pending (status -> credit) instead of
      // forcing immediate payment. Only meaningful for mode "extra".
      asCredit: false,
      // Same Patient Credit / Hospital Credit choice as the New Bill "Credit Bill"
      // flow (see useRefferalAsCredit below) — only surfaced when asCredit is on.
      creditType: "patient",
      creditHospitalId: "",
      creditHospitalName: "",
      reason: "",
    });
  }, [billing.overall_data, modifyContext.previous_bill_amount, modifyContext.previous_received_amount]);

  const submitModifyBill = useCallback(() => {
    const runModify = (effectivePatientId) => {
      const base = buildBillPayload("modify", effectivePatientId);
      const line_items = (base.line_items || []).map((li, idx) => {
        const src = billing.billingList[idx];
        return src?.bill_line_item_id ? { ...li, bill_line_item_id: src.bill_line_item_id } : li;
      });
      const payload = {
        bill_id: +modifyBillId,
        patient_id: base.patient_id,
        line_items,
        sub_total: base.taxable_amount,
        overall_concession_amount: base.overall_concession_amount,
        overall_concession_percent: base.overall_concession_percent,
        taxable_amount: base.taxable_amount,
        round_off: base.round_off,
        payable_amount: base.payable_amount,
        reason: (deltaPopup.reason || "").trim() || null,
      };
      if (deltaPopup.mode === "extra" && !deltaPopup.asCredit) {
        const f = deltaPopup.form;
        const totalMode = ["cash_amount", "card_amount", "upi_amount", "bank_amount", "cheque_amount"]
          .reduce((s, k) => s + (+f[k] || 0), 0);
        payload.extra_payment = {
          received_amount: totalMode,
          cash_amount: +f.cash_amount || 0,
          card_amount: +f.card_amount || 0,
          upi_amount: +f.upi_amount || 0,
          bank_amount: +f.bank_amount || 0,
          cheque_amount: +f.cheque_amount || 0,
          card_transaction_no: (f.card_transaction_no || "").trim(),
          upi_transaction_no: (f.upi_transaction_no || "").trim(),
          bank_transaction_no: (f.bank_transaction_no || "").trim(),
          cheque_transaction_no: (f.cheque_transaction_no || "").trim(),
        };
      }
      // mode === "extra" && asCredit -> no extra_payment sent; backend leaves
      // received_amount unchanged and recomputes status as "credit" (pending).
      // Same Patient/Hospital credit decision as the New Bill Credit Bill flow —
      // infer from the referral fields when the tenant doesn't need the explicit
      // popup, otherwise use what was picked inline in this dialog.
      if (deltaPopup.mode === "extra" && deltaPopup.asCredit) {
        let isCorporate;
        let corporate_id = null;
        if (useRefferalAsCredit) {
          isCorporate = refferals?.type === "doctor";
          corporate_id = refferals?.hospital_id !== "" && refferals?.hospital_id != null
            ? Number(refferals?.hospital_id)
            : null;
        } else {
          isCorporate = deltaPopup.creditType === "corporate";
          corporate_id = deltaPopup.creditHospitalId !== "" && deltaPopup.creditHospitalId != null
            ? Number(deltaPopup.creditHospitalId)
            : null;
        }
        payload.credit_type = isCorporate ? "corporate" : "patient";
        if (isCorporate && corporate_id != null) {
          payload.corporate_id = corporate_id;
        }
      }
      if (deltaPopup.mode === "refund") {
        const f = deltaPopup.form;
        const totalMode = ["cash_amount", "card_amount", "upi_amount", "bank_amount", "cheque_amount"]
          .reduce((s, k) => s + (+f[k] || 0), 0);
        payload.refund_payment = {
          refund_amount: totalMode,
          cash_amount: +f.cash_amount || 0,
          card_amount: +f.card_amount || 0,
          upi_amount: +f.upi_amount || 0,
          bank_amount: +f.bank_amount || 0,
          cheque_amount: +f.cheque_amount || 0,
          card_transaction_no: (f.card_transaction_no || "").trim(),
          upi_transaction_no: (f.upi_transaction_no || "").trim(),
          bank_transaction_no: (f.bank_transaction_no || "").trim(),
          cheque_transaction_no: (f.cheque_transaction_no || "").trim(),
        };
      }
      RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_BILLING_BILL_MODIFY)
        .then((res) => {
          setIsLoader(false);
          if (res?.data?.status === "success") {
            setDeltaPopup((p) => ({ ...p, open: false }));
            // Same success popup as New Bill; its Close returns to Overall Bills in modify mode.
            setBillSuccessPopup({
              open: true,
              label: res.data.message || "Bill modified successfully",
              billId: res?.data?.data?.bill_id ?? modifyBillId,
              nxtId: res?.data?.data?.nxt_id || "",
              biopsyNo: res?.data?.data?.biopsy_number || "",
            });
          } else {
            setMessage((prev) => ({ ...prev, isErrorMsg: true, isErrorMsgText: res?.data?.message || "Failed to modify bill" }));
          }
        })
        .catch((err) => {
          setIsLoader(false);
          setMessage((prev) => ({ ...prev, isErrorMsg: true, isErrorMsgText: err?.response?.data?.message || err?.message || "Failed to modify bill" }));
        });
    };

    setIsLoader(true);
    // Update patient details first (so name / dob / gender / mobile changes persist),
    // then update the bill via the modify endpoint.
    if (typeof getOrCreatePatientId === "function") {
      getOrCreatePatientId()
        .then((resolvedId) => {
          if (resolvedId) {
            runModify(resolvedId);
          } else {
            setIsLoader(false);
            setMessage((prev) => ({ ...prev, isErrorMsg: true, isErrorMsgText: "Patient could not be updated" }));
          }
        })
        .catch(() => {
          setIsLoader(false);
        });
    } else {
      runModify(patientId);
    }
  }, [buildBillPayload, patientId, billing.billingList, modifyBillId, deltaPopup, navigate, getOrCreatePatientId, useRefferalAsCredit, refferals]);

  const handleDraftSave = useCallback(() => {
    if (!patientId) {
      errorMessage("Please create or select a patient first");
      return;
    }
    if (!billing.billingList?.length) {
      errorMessage("Add at least one service to the bill");
      return;
    }
    const payload = buildBillPayload("draft");
    setIsLoader(true);
    RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_BILLING_BILL)
      .then((res) => {
        if (res?.data?.status === "success") {
          const billId = res?.data?.data?.id ?? res?.data?.id;
          setBillSuccessPopup({ open: true, label: res?.data?.message || "Bill created successfully", billId, nxtId: res?.data?.data?.nxt_id || "", biopsyNo: res?.data?.data?.biopsy_number || "" });
        }
        setIsLoader(false);
      })
      .catch((err) => {
        setIsLoader(false);
        errorMessage(err?.response?.data?.message || err?.message);
      });
  }, [patientId, billing.billingList?.length, buildBillPayload, errorMessage, successMessage]);

  const handleReceivePayment = useCallback(() => {
    if (!billing.billingList?.length) {
      errorMessage("Add at least one service to the bill");
      return;
    }
    if (!selectedCreatedBy?.username) {
      errorMessage("Please select Billed By");
      return;
    }
    const runBillCreation = (effectivePatientId) => {
      const validationMsg = checkPaymentValidation();
      if (validationMsg) {
        errorMessage(validationMsg);
        return;
      }
      const payload = buildBillPayload("paid", effectivePatientId);
      setIsLoader(true);
      RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_BILLING_BILL)
        .then((res) => {
          if (res?.data?.status === "success") {
            const billId = res?.data?.data?.id ?? res?.data?.id;
            setBillSuccessPopup({ open: true, label: res?.data?.message || "Payment received successfully", billId, nxtId: res?.data?.data?.nxt_id || "", biopsyNo: res?.data?.data?.biopsy_number || "" });
          }
          setIsLoader(false);
        })
        .catch((err) => {
          setIsLoader(false);
          errorMessage(err?.response?.data?.message || err?.message);
        });
    };
    if (typeof getOrCreatePatientId === "function") {
      setIsLoader(true);
      getOrCreatePatientId()
        .then((resolvedId) => {
          if (resolvedId) {
            runBillCreation(resolvedId);
          } else {
            setIsLoader(false);
            errorMessage("Patient could not be created");
          }
        })
        .catch(() => {
          setIsLoader(false);
        });
      return;
    }
    if (patientId) {
      runBillCreation(patientId);
      return;
    }
    errorMessage("Please create or select a patient first");
  }, [patientId, billing.billingList?.length, buildBillPayload, getOrCreatePatientId, errorMessage, successMessage]);

  const handleBillPrint = useCallback(() => {
    const billId = billSuccessPopup.billId;
    if (billId == null || billId === "") {
      errorMessage("Bill ID not available for print.");
      return;
    }
    const url = `${Serviceurls.HISTOPATHOLOGY_BILL_PRINT}?bill_id=${encodeURIComponent(billId)}`;
    RestAPIService.PrintPDF(url)
      .then((response) => {
        const file = new Blob([response.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      })
      .catch((err) => {
        errorMessage(err?.response?.data?.message || err?.message || "Failed to load bill print.");
      });
  }, [billSuccessPopup.billId, errorMessage]);

  const fullAmountEntered = () => {
    // Nothing to collect on a zero-amount bill — treat it as trivially
    // "fully entered" so Receive Payment (-> status "paid") is reachable
    // instead of forcing the user through Credit Bill.
    if (payableAmt <= 0) return true;
    const hasMode = paymentMode.isCash || paymentMode.isCard || paymentMode.isUPI || paymentMode.isBank || paymentMode.isCheque;
    if (!hasMode || payamt !== payableAmt) return false;
    if (!CommonValidation.isTransactionNumberMandatory()) return true;
    const cardAmt = +(paymentMode.card_amt || 0);
    const upiAmt = +(paymentMode.upi_amt || 0);
    const bankAmt = +(paymentMode.bank_amt || 0);
    const chequeAmt = +(paymentMode.cheque_amt || 0);
    const cardNo = (paymentMode.card_no || "").trim();
    const upiNo = (paymentMode.upi_no || "").trim();
    const bankNo = (paymentMode.bank_no || "").trim();
    const chequeNo = (paymentMode.cheque_no || "").trim();
    const refsValid =
      (cardAmt === 0 || cardNo !== "") &&
      (upiAmt === 0 || upiNo !== "") &&
      (bankAmt === 0 || bankNo !== "") &&
      (chequeAmt === 0 || chequeNo !== "");
    if (!refsValid) return false;
    const minLenValid =
      (cardNo === "" || cardNo.length >= TXN_MSG_MAX_LEN) &&
      (upiNo === "" || upiNo.length >= TXN_MSG_MAX_LEN) &&
      (bankNo === "" || bankNo.length >= TXN_MSG_MAX_LEN) &&
      (chequeNo === "" || chequeNo.length >= TXN_MSG_MAX_LEN);
    return minLenValid;
  };

  const checkPaymentValidation = () => {
    if (CommonValidation.isTransactionNumberMandatory()) {
      const cardAmt = +(paymentMode.card_amt || 0);
      const upiAmt = +(paymentMode.upi_amt || 0);
      const bankAmt = +(paymentMode.bank_amt || 0);
      const chequeAmt = +(paymentMode.cheque_amt || 0);
      const cardNo = (paymentMode.card_no || "").trim();
      const upiNo = (paymentMode.upi_no || "").trim();
      const bankNo = (paymentMode.bank_no || "").trim();
      const chequeNo = (paymentMode.cheque_no || "").trim();
      if (
        (cardAmt !== 0 && cardNo === "") ||
        (upiAmt !== 0 && upiNo === "") ||
        (bankAmt !== 0 && bankNo === "") ||
        (chequeAmt !== 0 && chequeNo === "")
      ) {
        return "Please, Enter Reference Number";
      }
    }
    const cardNo = (paymentMode.card_no || "").trim();
    const upiNo = (paymentMode.upi_no || "").trim();
    const bankNo = (paymentMode.bank_no || "").trim();
    const chequeNo = (paymentMode.cheque_no || "").trim();
    if (
      (cardNo !== "" && cardNo.length < TXN_MSG_MAX_LEN) ||
      (upiNo !== "" && upiNo.length < TXN_MSG_MAX_LEN) ||
      (bankNo !== "" && bankNo.length < TXN_MSG_MAX_LEN) ||
      (chequeNo !== "" && chequeNo.length < TXN_MSG_MAX_LEN)
    ) {
      return TXN_MSG_MAX;
    }
    return null;
  };

  const cashTextBox = (key, values, label, Icon) => (
    <TextField
      size="small"
      sx={{ width: "10dvw", mt: "0.5rem", ...INPUT_SIZE_SX }}
      value={values}
      label={`${label} (${CurrencySymbol})`}
      onChange={(e) => onPaymentChange(key, e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <img src={Icon?.default ?? Icon} alt={label} className="eMed_action_img_small" style={{ width: "1.2vw", height: "1.2vw" }} />
          </InputAdornment>
        ),
      }}
    />
  );

  const referenceTextBox = (key, values) => (
    <TextField
      size="small"
      sx={{ width: "13dvw", mt: "0.5rem", ml: "0.5rem", ...INPUT_SIZE_SX }}
      value={values}
      label={`Reference Number${CommonValidation.isTransactionNumberMandatory() ? " *" : ""}`}
      onChange={(e) => onPaymentChange(key, e.target.value)}
    />
  );

  const renderLftContent = () => (
    <Box component="div" sx={{ backgroundColor: "white", position: "relative", borderRadius: "6px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Referral Row */}
      <Box display="flex" alignItems="center" gap="0.8vw" sx={{ px: "0.8vw", py: "0.6vh", borderBottom: "1px solid #eee" }}>
        <FormControl size="small" sx={{ width: "10vw", ...INPUT_SIZE_SX, "& .MuiInputLabel-root": { overflow: "visible", fontSize: '0.95vw', top: '-0.3vw', marginTop: '0.1vw' } }}>
          <InputLabel>Referred Type</InputLabel>
          <Select
            value={refferals?.type || "self"}
            label="Referred Type"
            onChange={(e) => setRefferals({ ...refferals, type: e.target.value, doctor: "", hospital: "", hospital_id: "", op_ip_no: "" })}
          >
            <MenuItem value="self">Self</MenuItem>
            <MenuItem value="doctor">Doctor</MenuItem>
          </Select>
        </FormControl>
        <Autocomplete
          freeSolo
          autoSelect
          disabled={refferals?.type === "self"}
          size="small"
          sx={{ flex: 1, ...INPUT_SIZE_SX }}
          options={refferals?.refferalList || []}
          getOptionLabel={(opt) =>
            typeof opt === "string" ? opt : opt?.specialization ? `${opt?.name} - ${opt?.specialization}` : opt?.name || ""
          }
          value={refferals?.doctor || null}
          inputValue={
            typeof refferals?.doctor === "string"
              ? refferals.doctor
              : (refferals?.doctor?.specialization
                  ? `${refferals?.doctor?.name || ""} - ${refferals?.doctor?.specialization}`
                  : (refferals?.doctor?.name || ""))
          }
          onChange={(e, newValue) => {
            // newValue can be a string (free-typed) or a doctor object (picked).
            const picked = newValue && typeof newValue === "object" ? newValue : null;
            const hospIdFromDoc = picked?.hospital_id ?? picked?.hospital?.id ?? null;
            const docHospName = picked?.hospital_name || "";
            const matched = (refferals?.hospitalList || []).find(
              (h) => (hospIdFromDoc != null && Number(h.id) === Number(hospIdFromDoc))
                || (docHospName && h.name.toLowerCase() === docHospName.toLowerCase())
            );
            // Sync the ref immediately so validation reads the latest typed name
            // even if the React state update hasn't committed yet.
            doctorTypedRef.current = picked
              ? (picked.name || "")
              : (typeof newValue === "string" ? newValue : "");
            if (matched) hospitalTypedRef.current = matched.name;
            else if (docHospName) hospitalTypedRef.current = docHospName;
            setRefferals((prev) => ({
              ...prev,
              doctor: newValue ?? "",
              hospital: matched ? matched.name : docHospName || prev.hospital,
              hospital_id: matched ? matched.id : (hospIdFromDoc ?? prev.hospital_id),
            }));
          }}
          onInputChange={(e, newInputValue, reason) => {
            if (reason === "reset") return; // avoid wiping state on initial mount/value sync
            doctorTypedRef.current = newInputValue || "";
            setRefferals((prev) => ({ ...prev, doctor: newInputValue || "" }));
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Referred Doctor" inputProps={{ ...params.inputProps, maxLength: 40 }} />
          )}
        />
        <Autocomplete
          freeSolo
          autoSelect
          size="small"
          disabled={refferals?.type === "self"}
          sx={{ flex: 1, ...INPUT_SIZE_SX }}
          options={refferals?.hospitalList || []}
          getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.name || "")}
          value={refferals?.hospital || ""}
          inputValue={refferals?.hospital || ""}
          onChange={(_e, newVal) => {
            if (!newVal) {
              hospitalTypedRef.current = "";
              setRefferals((prev) => ({ ...prev, hospital: "", hospital_id: "" }));
            } else if (typeof newVal === "string") {
              hospitalTypedRef.current = newVal;
              setRefferals((prev) => ({ ...prev, hospital: newVal, hospital_id: "" }));
            } else {
              hospitalTypedRef.current = newVal.name || "";
              setRefferals((prev) => ({ ...prev, hospital: newVal.name || "", hospital_id: newVal.id ?? "" }));
            }
          }}
          onInputChange={(_e, val, reason) => {
            if (reason === "input") {
              hospitalTypedRef.current = val;
              setRefferals((prev) => ({ ...prev, hospital: val, hospital_id: "" }));
            } else if (reason === "clear") {
              hospitalTypedRef.current = "";
              setRefferals((prev) => ({ ...prev, hospital: "", hospital_id: "" }));
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="Referred Hospital" inputProps={{ ...params.inputProps, maxLength: 50 }} />
          )}
        />
        <TextField
          size="small"
          value={refferals?.op_ip_no || ""}
          inputProps={{ maxLength: 30 }}
          onChange={(e) => {
            opIpTypedRef.current = e.target.value;
            setRefferals((prev) => ({ ...prev, op_ip_no: e.target.value }));
          }}
          sx={{ flex: 1, ...INPUT_SIZE_SX }}
          label="OP / IP Number"
        />
      </Box>
      {/* Table */}
      <Box component="div" sx={{ height: isModifyBill ? "61dvh" : "64dvh", overflow: "auto", borderTop: "1px solid #eee" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {/* When Concession column is hidden, absorb its 11% into Service Description. */}
              <TableCell className="billing_tbl_head" width={needConcessionFlow ? "39%" : "50%"}>Service Description</TableCell>
              {/* Units column hidden UI-wise; unit key still sent as 1 in payload. */}
              <TableCell className="billing_tbl_head" width="11%">Rate/Unit</TableCell>
              {needConcessionFlow && <TableCell className="billing_tbl_head" width="11%">Concession</TableCell>}
              <TableCell className="billing_tbl_head" width="11%">{`Amount (${CurrencySymbol})`}</TableCell>
              {loginInfo?.is_gst_enabled && (
                <>
                  <TableCell className="billing_tbl_head" width="6.5%">GST (%)</TableCell>
                  <TableCell className="billing_tbl_head" width="11%">{`Net Amount(${CurrencySymbol})`}</TableCell>
                </>
              )}
              <TableCell className="billing_tbl_head" align="center" width="10.5%">
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Typography fontSize={COMMON_STYLES.FONT_SIZE_L} fontWeight={COMMON_STYLES.FONT_WEIGHT}>Action</Typography>
                  <Tooltip title={isModifyBill && modifyContext?.is_sample_collected ? "Locked after sample collection" : "Delete All"}>
                    <IconButton
                      sx={{ width: "2dvw", height: "2dvw", visibility: getCachevalue("BillType") === "Cancel" ? "hidden" : "visible" }}
                      disabled={billing.isEdit || (isModifyBill && modifyContext?.is_sample_collected)}
                      onClick={() => {
                        setBilling((prev) => ({
                          ...prev,
                          billingList: [],
                          cancelled_ids: [...prev.cancelled_ids, ...prev.billingList.map((it) => it.id).filter(Boolean)],
                          overallDiscount: { discount_amount: "", discount_percentage: "", discount_percentage_w_r: "" },
                        }));
                      }}
                    >
                      <DeleteForeverIcon color={billing.isEdit ? "disabled" : "error"} sx={{ width: "1.5dvw", height: "1.5dvw" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {billing.billingList?.length > 0 ? (
              billing.billingList.map((item, index) => {
                // When "Show only Service Name" is on, drop the "- head (code)" suffix
                // AND the "(svc_code)" trailing code from the composed label.
                const onlyName = getCachevalue("show_only_service_name") === "true";
                const serviceName = onlyName
                  ? (item?.service_name || "")
                  : `${item?.service_name && item?.service_code ? `${item.service_name} (${item.service_code})` : item?.service_name || ""} - ${item?.service_head_name && item?.service_head_code ? `${item.service_head_name} (${item.service_head_code})` : item?.service_head_name || ""}`;
                return (
                  <TableRow key={index}>
                    <TableCell className="billing_tbl_body">
                      {serviceName?.length > 40 ? (
                        <Tooltip title={serviceName}>
                          <Typography sx={{ fontSize: COMMON_STYLES.FONT_SIZE_MD }}>{serviceName.slice(0, 40)}...</Typography>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: COMMON_STYLES.FONT_SIZE_MD }}>{serviceName}</Typography>
                      )}
                      {item?.op_order_number && (
                        <Typography sx={{ fontSize: COMMON_STYLES.FONT_SIZE_MD }}>{item.op_order_number}</Typography>
                      )}
                    </TableCell>
                    {/* Units cell hidden UI-wise; unit value still stored/sent as 1 in payload. */}
                    <TableCell className="billing_tbl_body">{CommonValidation.formatAmount(item?.rate_per_unit || "")}</TableCell>
                    {needConcessionFlow && (
                      <TableCell className="billing_tbl_body">{CommonValidation.formatAmount(item?.concession_amt || "")}</TableCell>
                    )}
                    <TableCell className="billing_tbl_body">{CommonValidation.formatAmount(item?.total_with_discount || "")}</TableCell>
                    {loginInfo?.is_gst_enabled && (
                      <>
                        <TableCell className="billing_tbl_body">{item?.gst_percent}</TableCell>
                        <TableCell className="billing_tbl_body">{CommonValidation.formatAmount(item?.service_with_gst_total_amt || "")}</TableCell>
                      </>
                    )}
                    <TableCell align="center" className="billing_tbl_body">
                      {(() => {
                        const isApproved = isModifyBill && item?.bill_line_item_id && modifyContext.approved_line_ids?.has?.(item.bill_line_item_id);
                        const isWarn = isModifyBill && item?.bill_line_item_id && modifyContext.warn_line_ids?.has?.(item.bill_line_item_id);
                        return (
                          <Box sx={{ visibility: getCachevalue("BillType") === "Cancel" ? "hidden" : "visible", display: "inline-flex", alignItems: "center" }}>
                            {isWarn && (
                              <Tooltip title="Result in progress — edits may impact reporting" placement="top" arrow>
                                <Typography component="span" sx={{ color: "warning.main", mr: 0.5, fontSize: "1vw" }}>⚠</Typography>
                              </Tooltip>
                            )}
                            <Tooltip title={isApproved ? "Approved result — cannot be edited" : ""}>
                              <span>
                                <IconButton
                                  sx={{ width: "2dvw", height: "2dvw" }}
                                  disabled={(requestState?.fromApprover && !requestState.modifyApprover) || isApproved}
                                  onClick={() => onEditHandler(item, index)}
                                >
                                  <EditIcon color={(requestState?.fromApprover && !requestState.modifyApprover) || isApproved ? "disabled" : "primary"} sx={{ width: "1.5dvw", height: "1.5dvw" }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {!requestState.fromApprover && !requestState?.fromBillConvert && (() => {
                              // Post-collection: no delete allowed either — the service is
                              // pinned to a collected sample and any related result rows.
                              const lockedByCollection = isModifyBill && modifyContext?.is_sample_collected;
                              const disabled = billing.isEdit || isApproved || lockedByCollection;
                              const tip = isApproved
                                ? "Approved result — cannot be removed"
                                : (lockedByCollection ? "Locked after sample collection" : "");
                              return (
                                <Tooltip title={tip}>
                                  <span>
                                    <IconButton
                                      sx={{ width: "2dvw", height: "2dvw" }}
                                      disabled={disabled}
                                      onClick={() => {
                                        setBilling((prev) => {
                                          const list = [...prev.billingList];
                                          const cancelId = list[index]?.id ? [list[index].id] : [];
                                          list.splice(index, 1);
                                          return {
                                            ...prev,
                                            billingList: list,
                                            cancelled_ids: [...prev.cancelled_ids, ...cancelId],
                                            overallDiscount: { discount_amount: "", discount_percentage: "", discount_percentage_w_r: "" },
                                          };
                                        });
                                      }}
                                    >
                                      <DeleteForeverIcon color={disabled ? "disabled" : "error"} sx={{ width: "1.5dvw", height: "1.5dvw" }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              );
                            })()}
                          </Box>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={(loginInfo?.is_gst_enabled ? 9 : 7) - (needConcessionFlow ? 0 : 1)} align="center" sx={{ borderBottom: "none", position: "relative", top: "10rem" }}>
                  No Records To Be Shown
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {/* Service Input Bar */}
      <Box
        display="flex"
        flexWrap="nowrap"
        alignItems="center"
        gap="0.5vw"
        sx={{ px: "0.8vw", py: "0.5vh", borderTop: "1px solid #eee", minHeight: "3vw" }}
      >
        <Autocomplete
          size="small"
          sx={{ width: "9.5dvw", minWidth: 108, flexShrink: 0, ...INPUT_SIZE_SX }}
          options={billing.SpecimenList || []}
          getOptionLabel={(opt) =>
            typeof opt === "string" ? opt : (opt?.specimen_type || "-")
          }
          value={billing.selected_specimen ?? (billing.billingList?.[0]?.specimen_id && billing.SpecimenList?.length ? billing.SpecimenList.find((s) => s.id === billing.billingList[0].specimen_id) ?? null : null)}
          onChange={(e, newValue) => {
            if (newValue) setBilling((prev) => ({ ...prev, selected_specimen: newValue, selected_specimen_id: newValue?.id }));
            else setBilling((prev) => ({ ...prev, selected_specimen: null, selected_specimen_id: null }));
            clearService();
          }}
          disabled={
            // One bill = one specimen: locked once any line exists, in Modify Bill
            // too. To switch specimen, remove the existing services first.
            // Existing-bill add-ons (on screen or being modified) never change specimen:
            // they belong to their NXT ID's already-collected specimen.
            billing.billingList?.length > 0 || isExistingBill || !!modifyContext?.is_existing_bill
          }
          renderInput={(params) => <TextField {...params} label="Specimen Type" />}
        />
        <Autocomplete
          size="small"
          // When Concession flow is hidden, the two Conc textboxes disappear
          // (~12dvw of freed space). Grow the Search Service field to soak it up.
          sx={{ width: needConcessionFlow ? "16dvw" : "28dvw", minWidth: 200, flexShrink: 0, ...INPUT_SIZE_SX }}
          // Normally locked during an in-row edit (pencil hydrates the fields
          // for amount/discount tweaks only). In Modify Bill, unlock it too so
          // the user can swap the service on an existing line — but only when
          // the sample hasn't been collected yet. Post-collection the service
          // must stay pinned to what the sample was collected against.
          disabled={
            requestState.fromApprover ||
            (billing.isEdit && !(isModifyBill && !modifyContext?.is_sample_collected))
          }
          options={filteredServiceSugg}
          getOptionLabel={(opt) => {
            if (typeof opt === "string") return opt;
            if (getCachevalue("show_only_service_name") === "true") {
              return opt?.service_name || "";
            }
            const headPart = (opt?.service_head ?? opt?.service_head_name) && opt?.service_head_code
              ? `${opt?.service_head ?? opt?.service_head_name} (${opt.service_head_code})`
              : (opt?.service_head ?? opt?.service_head_name) || "";
            const serPart = opt?.service_name && opt?.service_code
              ? `${opt.service_name} (${opt.service_code})`
              : opt?.service_name || "";
            return `${headPart} - ${serPart}`;
          }}
          value={billing.selected_service}
          onChange={(e, newValue) => {
            if (newValue) {
              onSelectServiceHandler(newValue);
              setSearchKey("");
            } else clearService();
          }}
          onInputChange={(e, newInputValue) => {
            if (newInputValue?.length >= 3) setSearchKey(newInputValue);
          }}
          renderInput={(params) => <TextField {...params} label="Search Service" />}
          slotProps={{ popper: { style: { width: "30dvw" } } }}
          renderOption={(props, item) => {
            const onlyName = getCachevalue("show_only_service_name") === "true";
            const serviceName = item.service_head && item.service_head_code ? `${item.service_head} (${item.service_head_code})` : item.service_head || "";
            const serName = item.service_name && item.service_code ? `${item.service_name} (${item.service_code})` : item.service_name || "";
            const displayName = onlyName ? (item.service_name || "") : `${serviceName} ${serName}`;
            return (
              <Box {...props} key={item?.service_details_id}>
                <Tooltip title={displayName} arrow>
                  <Typography sx={{ fontSize: "0.9dvw", fontWeight: 600 }}>{displayName.length > 58 ? `${displayName.slice(0, 55)}...` : displayName}</Typography>
                </Tooltip>
                {!onlyName && (
                  <Typography sx={{ fontSize: "0.8dvw" }}>{item?.module_type === "IP_OP" ? "IP & OP" : item?.module_type || ""}</Typography>
                )}
              </Box>
            );
          }}
        />
        {/* Units stepper hidden UI-wise; billing.service_data.unit stays at
            its default (1) so payloads and downstream math are unchanged. */}
        {!!billing?.service_data?.doctor_amt || !!billing?.service_data?.hospital_amt || !!billing?.service_data?.is_sharable ? (
            <Button
              className="Common_Btn_Min_Width"
              sx={{ textTransform: "capitalize", height: "2.25vw", flexShrink: 0 }}
              disabled={!billing?.service_data?.edit_amount}
              endIcon={`(${CommonValidation.formatAmount(billing?.service_data?.rate_per_unit)})`}
              onClick={() => onOpenPopupHandler("rate")}
            >
            Rate/Unit
          </Button>
        ) : (
          <TextField
            autoComplete="off"
            size="small"
            InputProps={{ readOnly: !billing?.service_data?.edit_amount }}
            sx={{ width: "7dvw", ...INPUT_SIZE_SX }}
            label="Rate/Unit"
            value={billing?.service_data?.rate_per_unit ?? ""}
            onChange={(e) => withOutShareHandler(e, "rate_per_unit")}
          />
        )}
        {needConcessionFlow && (
        <Box component="div" display="flex" alignItems="center" gap={0.5} flexShrink={0}>
          {!!billing?.service_data?.doctor_amt || !!billing?.service_data?.hospital_amt ? (
            <Button
              className="Common_Btn_Min_Width"
              sx={{ textTransform: "capitalize" }}
              disabled={!billing?.service_data?.edit_discount}
              endIcon={`(${CommonValidation.formatAmount(billing?.service_data?.concession_amt)})`}
              onClick={() => onOpenPopupHandler("concession")}
            >
              Concession
            </Button>
          ) : (
            <>
              <TextField
                disabled={!billing?.service_data?.edit_discount}
                autoComplete="off"
                size="small"
                sx={{ width: "6dvw", ...INPUT_SIZE_SX }}
                label={`Conc (${CurrencySymbol})`}
                value={billing?.service_data?.concession_amt != null && billing.service_data.concession_amt !== "" ? billing.service_data.concession_amt : ""}
                onChange={(e) => withOutShareHandler(e, "concession_discount_amt")}
              />
              <TextField
                disabled={!billing?.service_data?.edit_discount}
                autoComplete="off"
                size="small"
                sx={{ width: "6dvw", ...INPUT_SIZE_SX }}
                label="Conc (%)"
                value={billing?.service_data?.concession_percent_w_r != null && billing.service_data.concession_percent_w_r !== "" ? billing.service_data.concession_percent_w_r : ""}
                onChange={(e) => withOutShareHandler(e, "concession_discount_percent")}
              />
            </>
          )}
        </Box>
        )}
        <TextField
          size="small"
          sx={{ width: "7dvw", ...INPUT_SIZE_SX }}
          value={
            billing?.service_data?.total_amt != null && billing.service_data.total_amt !== ""
              ? CommonValidation.formatAmount(+billing.service_data.total_amt - +(billing.service_data.concession_amt || 0))
              : ""
          }
          InputProps={{ readOnly: true }}
          label="Total"
        />
        {(() => {
          // Post-collection Modify Bill: block ONLY the "add new line" path.
          // In-place edit-save (billing.isEdit=true) stays open so users can
          // still adjust amount / discount / units of existing lines.
          const blockAddNew = !billing.isEdit && isModifyBill && modifyContext?.is_sample_collected;
          return (
            <Tooltip title={blockAddNew ? "Locked after sample collection" : ""}>
              <span>
                <IconButton
                  disabled={blockAddNew}
                  sx={{ height: "2.25vw", width: "2.25vw", flexShrink: 0, backgroundColor: blockAddNew ? "action.disabledBackground" : "primary.main", color: "white", borderRadius: "4px", "&:hover": { backgroundColor: blockAddNew ? "action.disabledBackground" : "primary.dark" } }}
                  onClick={onAddHandler}
                  aria-label="Add service"
                >
                  <AddIcon sx={{ fontSize: "1.2vw" }} />
                </IconButton>
              </span>
            </Tooltip>
          );
        })()}
      </Box>
    </Box>
  );

  const showCashTextBox = () => (
    <Box component="div">
      <Box component="div">
        <FormControlLabel control={<Checkbox size="small" checked={paymentMode?.isCash} onChange={(e) => handleCheckBox(e, "isCash")} />} label={<Typography fontSize={COMMON_STYLES.FONT_SIZE_L}>Cash</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={paymentMode?.isCard} onChange={(e) => handleCheckBox(e, "isCard")} />} label={<Typography fontSize={COMMON_STYLES.FONT_SIZE_L}>Card</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={paymentMode?.isUPI} onChange={(e) => handleCheckBox(e, "isUPI")} />} label={<Typography fontSize={COMMON_STYLES.FONT_SIZE_L}>UPI</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={paymentMode?.isBank} onChange={(e) => handleCheckBox(e, "isBank")} />} label={<Typography fontSize={COMMON_STYLES.FONT_SIZE_L}>Bank</Typography>} />
        <FormControlLabel control={<Checkbox size="small" checked={paymentMode?.isCheque} onChange={(e) => handleCheckBox(e, "isCheque")} />} label={<Typography fontSize={COMMON_STYLES.FONT_SIZE_L}>Cheque</Typography>} />
      </Box>
      <Box component="div">
        {paymentMode?.isCash && cashTextBox("cash_amt", paymentMode.cash_amt, "Cash Amount", ImagePaths.CashIcon)}
        {paymentMode?.isCard && (
          <Box display="flex">
            {cashTextBox("card_amt", paymentMode.card_amt, "Card Amount", ImagePaths.CardIcon)}
            {referenceTextBox("card_no", paymentMode.card_no)}
          </Box>
        )}
        {paymentMode?.isUPI && (
          <Box display="flex">
            {cashTextBox("upi_amt", paymentMode.upi_amt, "UPI Amount", ImagePaths.UPIIcon)}
            {referenceTextBox("upi_no", paymentMode.upi_no)}
          </Box>
        )}
        {paymentMode?.isBank && (
          <Box display="flex">
            {cashTextBox("bank_amt", paymentMode.bank_amt, "Bank Amount", ImagePaths.BankTransferIcon)}
            {referenceTextBox("bank_no", paymentMode.bank_no)}
          </Box>
        )}
        {paymentMode?.isCheque && (
          <Box display="flex">
            {cashTextBox("cheque_amt", paymentMode.cheque_amt, "Cheque Amount", ImagePaths.ChequeIcon)}
            {referenceTextBox("cheque_no", paymentMode.cheque_no)}
          </Box>
        )}
      </Box>
    </Box>
  );

  const postCreditBill = useCallback((effectivePatientId) => {
    const payload = buildBillPayload("not paid", effectivePatientId);
    setIsLoader(true);
    RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_BILLING_BILL)
      .then((res) => {
        setIsLoader(false);
        if (res?.data?.status === "success") {
          const billId = res?.data?.data?.id ?? res?.data?.id;
          setBillSuccessPopup({
            open: true,
            label: res?.data?.message || "Credit bill created successfully",
            billId,
            nxtId: res?.data?.data?.nxt_id || "",
            biopsyNo: res?.data?.data?.biopsy_number || "",
          });
        } else {
          errorMessage(res?.data?.message || "Failed to create credit bill.");
        }
      })
      .catch((err) => {
        setIsLoader(false);
        errorMessage(err?.response?.data?.message || err?.message);
      });
  }, [buildBillPayload, errorMessage]);

  const handleCreditBillClick = () => {
    if (!billing.billingList?.length) {
      errorMessage("Add at least one service to the bill");
      return;
    }
    if (!selectedCreatedBy?.username) {
      errorMessage("Please select Billed By");
      return;
    }
    if (refferals?.type === "doctor") {
      const docNameState = typeof refferals?.doctor === "string"
        ? refferals.doctor
        : (refferals?.doctor?.name || "");
      const docName = (doctorTypedRef.current || docNameState || "").trim();
      const hospName = (hospitalTypedRef.current || refferals?.hospital || "").trim();
      if (!docName) {
        errorMessage("Enter the referring doctor name.");
        return;
      }
      if (!hospName) {
        errorMessage("Referred Hospital is mandatory for Doctor referral.");
        return;
      }
    }
    if (typeof getOrCreatePatientId === "function") {
      setIsLoader(true);
      getOrCreatePatientId()
        .then((resolvedId) => {
          if (resolvedId) {
            postCreditBill(resolvedId);
          } else {
            setIsLoader(false);
            errorMessage("Patient could not be created");
          }
        })
        .catch(() => setIsLoader(false));
      return;
    }
    if (patientId) {
      postCreditBill(patientId);
      return;
    }
    errorMessage("Please create or select a patient first");
  };

  const openCreditPopup = () => {
    if (!billing.billingList?.length) {
      errorMessage("Add at least one service to the bill");
      return;
    }
    setPopupCreditType("patient");
    setPopupHospitalId("");
    setPopupHospitalName("");
    setPopupOpIpNo("");
    setCreditPopupOpen(true);
  };

  const handleCreditPopupDone = () => {
    if (popupCreditType === "corporate") {
      if (!String(popupHospitalName || "").trim()) {
        errorMessage("Hospital is mandatory.");
        return;
      }
      if (!String(popupOpIpNo || "").trim()) {
        errorMessage("OP / IP Number is mandatory.");
        return;
      }
    }
    setCreditPopupOpen(false);
    if (typeof getOrCreatePatientId === "function") {
      setIsLoader(true);
      getOrCreatePatientId()
        .then((resolvedId) => {
          if (resolvedId) postCreditBill(resolvedId);
          else { setIsLoader(false); errorMessage("Patient could not be created"); }
        })
        .catch(() => setIsLoader(false));
      return;
    }
    if (patientId) {
      postCreditBill(patientId);
      return;
    }
    errorMessage("Please create or select a patient first");
  };

  const renderCreditPopup = () => (
    <Dialog open={creditPopupOpen} onClose={() => setCreditPopupOpen(false)} fullWidth maxWidth="sm">
      <Box sx={{ p: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" borderBottom="2px solid lightgray" pb={1} mb={1.5}>
          <Typography fontSize="1dvw" fontWeight={600}>Select Credit Type</Typography>
          <IconButton onClick={() => setCreditPopupOpen(false)} size="small">
            <Box component="img" src={ImagePaths.LabCloseButton?.default ?? ImagePaths.LabCloseButton} alt="close" style={{ height: "1.2vw", width: "1.2vw" }} />
          </IconButton>
        </Box>
        <RadioGroup
          row
          value={popupCreditType}
          onChange={(e) => setPopupCreditType(e.target.value)}
          sx={{ gap: 2, mb: 1.5 }}
        >
          <FormControlLabel value="patient" control={<Radio size="small" />} label="Patient Credit" />
          <FormControlLabel value="corporate" control={<Radio size="small" />} label="Hospital Credit" />
        </RadioGroup>
        {popupCreditType === "corporate" && (
          <Box display="flex" flexWrap="wrap" gap={2} mb={1.5}>
            <Autocomplete
              freeSolo
              autoSelect
              size="small"
              sx={{ minWidth: "14vw", flex: 1, ...INPUT_SIZE_SX }}
              options={refferals?.hospitalList || []}
              getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.name || "")}
              value={popupHospitalName || ""}
              inputValue={popupHospitalName || ""}
              onChange={(_e, newVal) => {
                if (!newVal) { setPopupHospitalName(""); setPopupHospitalId(""); }
                else if (typeof newVal === "string") {
                  setPopupHospitalName(newVal);
                  setPopupHospitalId("");
                } else {
                  setPopupHospitalName(newVal.name || "");
                  setPopupHospitalId(newVal.id ?? "");
                }
              }}
              onInputChange={(_e, val, reason) => {
                if (reason === "input") {
                  setPopupHospitalName(val);
                  setPopupHospitalId("");
                } else if (reason === "clear") {
                  setPopupHospitalName("");
                  setPopupHospitalId("");
                }
              }}
              renderInput={(params) => <TextField {...params} label="Hospital *" />}
            />
            <TextField
              size="small"
              label="OP / IP Number *"
              sx={{ minWidth: "14vw", flex: 1, ...INPUT_SIZE_SX }}
              value={popupOpIpNo || ""}
              onChange={(e) => setPopupOpIpNo(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 30 }}
            />
          </Box>
        )}
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="contained" size="small" onClick={handleCreditPopupDone}>Done</Button>
        </Box>
      </Box>
    </Dialog>
  );

  const renderRgtContent = () => {
    const concessionApplicable = +(billing.overall_data?.concession_applicable ?? 0);
    const taxableDisplay = (+(billing.overall_data?.taxable_amount ?? 0)) - +(billing.overallDiscount?.discount_amount ?? 0);
    // `needConcessionFlow` is declared at the component top; alias here for readability.
    const needConcession = needConcessionFlow;

    const summaryRow = (label, value, bold) => (
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ py: "0.2vw" }}>
        <Typography sx={{ fontSize: "0.8vw", fontWeight: bold ? 700 : 400, color: bold ? "#1a1a1a" : "#555", flex: "1 1 auto", minWidth: 0 }}>{label}</Typography>
        <Typography sx={{ fontSize: "0.8vw", fontWeight: bold ? 700 : 500, color: bold ? "#1a1a1a" : "#333", flexShrink: 0, textAlign: "right", minWidth: "4vw" }}>{`${CurrencySymbol} ${CommonValidation.formatAmount(value)}`}</Typography>
      </Box>
    );

    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", gap: "0.5vh" }}>
        {/* Summary Card */}
        <Box sx={{ backgroundColor: "white", borderRadius: "6px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", p: "0.6vw" }}>
          {needConcession && summaryRow("Sub Amount", billing.overall_data?.sub_total)}
          {needConcession && summaryRow("Concession Applicable", billing.overall_data?.concession_applicable)}

          {/* Overall Concession Row */}
          {needConcession && (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ py: "0.2vw" }}>
            <Typography sx={{ fontSize: "0.8vw", color: "#555", flexShrink: 0 }}>Overall Concession</Typography>
            <Box display="flex" alignItems="center" gap="0.3vw">
              <TextField
                autoComplete="off"
                size="small"
                sx={{ width: "4vw", ...INPUT_SIZE_SX }}
                disabled={getCachevalue("BillType") === "Cancel" || billing.isEdit || +concessionApplicable === 0}
                placeholder={CurrencySymbol}
                value={billing?.overallDiscount?.discount_amount ?? ""}
                onBlur={() => {}}
                onChange={(e) => {
                  const value = e.target.value;
                  if (+concessionApplicable === 0) return;
                  if (CommonValidation.DecimalNumber(value) && ((+value >= 0 && +value <= +concessionApplicable) || value === "")) {
                    setBilling((prev) => {
                      const pct = value && +concessionApplicable > 0
                        ? CommonValidation.getDiscountPercentage(value, concessionApplicable)
                        : "";
                      const pctRound = value && +concessionApplicable > 0
                        ? CommonValidation.getDiscountPercentageWithRound(value, concessionApplicable)
                        : "";
                      const newDiscount = {
                        ...prev.overallDiscount,
                        discount_amount: value,
                        discount_percentage: Number.isFinite(+pct) ? pct : "",
                        discount_percentage_w_r: Number.isFinite(+pctRound) ? pctRound : "",
                      };
                      return { ...prev, overallDiscount: newDiscount };
                    });
                  }
                }}
              />
              <TextField
                autoComplete="off"
                size="small"
                sx={{ width: "4vw", ...INPUT_SIZE_SX }}
                disabled={getCachevalue("BillType") === "Cancel" || billing.isEdit || +concessionApplicable === 0}
                placeholder="%"
                value={billing?.overallDiscount?.discount_percentage_w_r ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (+concessionApplicable === 0) return;
                  if ((CommonValidation.DecimalNumber(value) && +value >= 0 && +value <= 100 && +concessionApplicable !== 0) || value === "") {
                    setBilling((prev) => {
                      const findDisAmt = value && +concessionApplicable > 0
                        ? CommonValidation.getDiscountAmountWithRound(value, concessionApplicable)
                        : "";
                      const newDiscount = {
                        ...prev.overallDiscount,
                        discount_percentage: value,
                        discount_percentage_w_r: value,
                        discount_amount: Number.isFinite(+findDisAmt) ? findDisAmt : "",
                      };
                      return { ...prev, overallDiscount: newDiscount };
                    });
                  }
                }}
              />
              {/* <IconButton sx={{ height: "1.8vw", width: "1.8vw" }} size="small">
                <DescriptionIcon color="primary" sx={{ fontSize: "1vw" }} />
              </IconButton> */}
            </Box>
          </Box>
          )}

          {needConcession && summaryRow("Taxable Amount", taxableDisplay)}
          {loginInfo?.is_gst_enabled && summaryRow("GST Amount", billing.overall_data?.gst_amount)}
          {summaryRow("Round Off", billing.overall_data?.round_off)}

          {/* Payable Amount - highlighted */}
          <Box sx={{ mt: "0.3vw", pt: "0.4vw", borderTop: "1.5px solid #e0e0e0" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: "0.95vw", fontWeight: 700, color: "#1a1a1a" }}>Payable Amount</Typography>
              <Typography sx={{ fontSize: "0.95vw", fontWeight: 700, color: "primary.main" }}>{`${CurrencySymbol} ${CommonValidation.formatAmount(billing.overall_data?.payable_amt)}`}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Payment Methods Card — hidden in Modify Bill mode; money movement (extra pay / refund) is handled in the Update Bill delta popup instead. */}
        {!isModifyBill && (
          <Box sx={{ backgroundColor: "white", borderRadius: "6px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", p: "0.6vw", overflowY: "auto", height: "38dvh" }}>
            <Autocomplete
              size="small"
              sx={{ mb: "0.5vh", ...INPUT_SIZE_SX }}
              options={userList}
              getOptionLabel={(opt) => {
                if (!opt) return "";
                if (typeof opt === "string") return opt;
                const name = [opt.first_name, opt.last_name].filter(Boolean).join(" ");
                return name || opt.username || "";
              }}
              isOptionEqualToValue={(a, b) => a?.id === b?.id || a?.username === b?.username}
              value={selectedCreatedBy}
              onChange={(_e, newVal) => setSelectedCreatedBy(newVal || null)}
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label="Billed By" required error={!selectedCreatedBy} />
              )}
            />
            {showCashTextBox()}
          </Box>
        )}

        {/* Action Buttons */}
        {canEdit && (
          <Box display="flex" flexDirection="column" gap="0.3vw" sx={{ mt: isModifyBill ? "auto" : 0 }}>
            {isModifyBill ? (
              <Button
                variant="contained"
                fullWidth
                disableElevation
                sx={{ height: "2.25vw", textTransform: "capitalize", fontSize: "0.85vw", fontWeight: 600, borderRadius: "4px" }}
                onClick={openModifyDelta}
              >
                Update Bill
              </Button>
            ) : (
              <>
                <Box display="flex" flexDirection="row" gap="0.3vw">
                  <Button
                    variant="contained"
                    fullWidth
                    disableElevation
                    sx={{ height: "2.25vw", textTransform: "capitalize", fontSize: "0.85vw", fontWeight: 600, borderRadius: "4px" }}
                    disabled={!fullAmountEntered()}
                    onClick={() => handleReceivePayment()}
                  >
                    Receive Payment
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    sx={{ height: "2.25vw", textTransform: "capitalize", fontSize: "0.85vw", borderRadius: "4px" }}
                    // Nothing to credit if the entered payment already covers the
                    // full payable amount — force the user through Receive Payment.
                    disabled={fullAmountEntered()}
                    onClick={useRefferalAsCredit ? handleCreditBillClick : openCreditPopup}
                  >
                    Credit Bill
                  </Button>
                </Box>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{ height: "2.25vw", textTransform: "capitalize", fontSize: "0.85vw", borderRadius: "4px" }}
                  onClick={() => onBillSuccessClear?.()}
                >
                  Clear
                </Button>
              </>
            )}
          </Box>
        )}
        {renderCreditPopup()}
      </Box>
    );
  };

  return (
    <Box component="div" sx={{ overflow: "hidden" }}>
      <Grid container spacing={0} sx={{ height: "100%" }}>
        <Grid item xs={8.5} sx={{ display: "flex", flexDirection: "column" }}>
          <Box component="div" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {renderLftContent()}
          </Box>
        </Grid>
        <Grid item xs={3.5}>
          <Box sx={{ pl: "0.5vw" }}>
            {renderRgtContent()}
          </Box>
        </Grid>
      </Grid>
      {message.isErrorMsg && <ToastMsg severity="error" msg={message.isErrorMsgText} msgPop={msgClose} />}
      {message.successMsg && <ToastMsg severity="success" msg={message.successMsgText} msgPop={msgClose} />}
      {billSuccessPopup.open && (
        <Modal open>
          <Box component="div" className="eMed_Bill_Success_Preview_Main" sx={{ height: "auto", minHeight: "16vw", pt: "0.5vw" }}>
            <img
              className="eMed_Bill_Success_animation"
              src={ImagePaths.SuccessAnimationGif?.default ?? ImagePaths.SuccessAnimationGif}
              alt="Success"
            />
            <Typography fontWeight={600}>{billSuccessPopup.label}</Typography>
            {oneByOneCollectionFlow && (billSuccessPopup.nxtId || billSuccessPopup.biopsyNo) && (
              <Box display="flex" gap={3} justifyContent="center" mt={0.5}>
                {billSuccessPopup.nxtId && (
                  <Typography variant="body2"><b>NXT ID:</b> {billSuccessPopup.nxtId}</Typography>
                )}
                {billSuccessPopup.biopsyNo && (
                  <Typography variant="body2"><b>Biopsy Number:</b> {billSuccessPopup.biopsyNo}</Typography>
                )}
              </Box>
            )}
            <Box display="flex" width="100%" justifyContent="center" alignItems="center" gap={1.5} padding="1vw" flexWrap="wrap">
              <Button
                size="small"
                variant="contained"
                sx={{ textTransform: "capitalize", minWidth: "7dvw" }}
                onClick={handleBillPrint}
                disabled={billSuccessPopup.billId == null || billSuccessPopup.billId === ""}
              >
                Print Bill
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={{ textTransform: "capitalize", minWidth: "7dvw" }}
                onClick={() => {
                  setBillSuccessPopup({ open: false, label: "" });
                  if (isModifyBill) {
                    navigate("/HistopathologyBillingTransaction/OverallBills");
                  } else {
                    onBillSuccessClear?.();
                  }
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
      {/* Modify Bill Delta Popup */}
      <Dialog
        open={!!deltaPopup.open}
        onClose={() => setDeltaPopup((p) => ({ ...p, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1vw", fontWeight: 700 }}>Modify Bill Summary</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 0.5 }}>
            <Typography sx={{ fontSize: "0.9vw" }}>
              Previous Total: <b>{CommonValidation.AmountFormat(deltaPopup.previous || 0)}</b>
              {"  →  "}
              New Total: <b>{CommonValidation.AmountFormat(deltaPopup.next || 0)}</b>
              {"  (Difference: "}
              <b style={{ color: (deltaPopup.delta || 0) >= 0 ? "#2e7d32" : "#d32f2f" }}>
                {CommonValidation.AmountFormat(deltaPopup.delta || 0)}
              </b>
              {")"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Already Received: {CommonValidation.AmountFormat(modifyContext.previous_received_amount || 0)}
            </Typography>
            {(deltaPopup.mode === "extra" || deltaPopup.mode === "refund") && (() => {
              const isRefund = deltaPopup.mode === "refund";
              const required = +(isRefund ? deltaPopup.refundNeeded : deltaPopup.extraNeeded) || 0;
              const modes = ["cash", "card", "upi", "bank", "cheque"];
              const handleAmount = (m) => (e) => {
                const v = e.target.value;
                if (v !== "" && !CommonValidation.DecimalNumber(v)) return;
                const others = modes
                  .filter((k) => k !== m)
                  .reduce((s, k) => s + (+((deltaPopup.form || {})[`${k}_amount`]) || 0), 0);
                const newVal = +v || 0;
                if (v !== "" && others + newVal > required) return;
                setDeltaPopup((p) => ({ ...p, form: { ...p.form, [`${m}_amount`]: v } }));
              };
              return (
                <>
                  <Typography sx={{ fontSize: "0.9vw", fontWeight: 600, color: isRefund ? "#d32f2f" : "#2e7d32", mt: 1 }}>
                    {isRefund ? "Refund to issue" : "Additional payment required"}: {CommonValidation.AmountFormat(required)}
                  </Typography>
                  {!isRefund && (
                    <FormControlLabel
                      sx={{ mt: -0.5 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={!!deltaPopup.asCredit}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setDeltaPopup((p) => ({
                              ...p,
                              asCredit: checked,
                              // Clear any partially-entered payment amounts so they
                              // can't leak into the payload once credit is chosen.
                              form: checked
                                ? {
                                    ...p.form,
                                    cash_amount: "", card_amount: "", upi_amount: "", bank_amount: "", cheque_amount: "",
                                    card_transaction_no: "", upi_transaction_no: "", bank_transaction_no: "", cheque_transaction_no: "",
                                  }
                                : p.form,
                            }));
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: "0.85vw" }}>
                          Add to Credit / Pending (don't collect now)
                        </Typography>
                      }
                    />
                  )}
                  {/* Same Patient Credit / Hospital Credit choice as New Bill's Credit Bill flow.
                      When the tenant doesn't need the explicit popup there (useRefferalAsCredit),
                      the credit type is inferred from the referral fields instead — no extra UI. */}
                  {deltaPopup.asCredit && !useRefferalAsCredit && (
                    <Box sx={{ pl: 4, mt: -0.5, mb: 1 }}>
                      <RadioGroup
                        row
                        value={deltaPopup.creditType || "patient"}
                        onChange={(e) => setDeltaPopup((p) => ({ ...p, creditType: e.target.value }))}
                        sx={{ gap: 2 }}
                      >
                        <FormControlLabel value="patient" control={<Radio size="small" />} label="Patient Credit" />
                        <FormControlLabel value="corporate" control={<Radio size="small" />} label="Hospital Credit" />
                      </RadioGroup>
                      {deltaPopup.creditType === "corporate" && (
                        <Autocomplete
                          freeSolo
                          autoSelect
                          size="small"
                          sx={{ mt: 0.5, ...INPUT_SIZE_SX }}
                          options={refferals?.hospitalList || []}
                          getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.name || "")}
                          value={deltaPopup.creditHospitalName || ""}
                          inputValue={deltaPopup.creditHospitalName || ""}
                          onChange={(_e, newVal) => {
                            if (!newVal) setDeltaPopup((p) => ({ ...p, creditHospitalName: "", creditHospitalId: "" }));
                            else if (typeof newVal === "string") setDeltaPopup((p) => ({ ...p, creditHospitalName: newVal, creditHospitalId: "" }));
                            else setDeltaPopup((p) => ({ ...p, creditHospitalName: newVal.name || "", creditHospitalId: newVal.id ?? "" }));
                          }}
                          onInputChange={(_e, val, reason) => {
                            if (reason === "input") setDeltaPopup((p) => ({ ...p, creditHospitalName: val, creditHospitalId: "" }));
                            else if (reason === "clear") setDeltaPopup((p) => ({ ...p, creditHospitalName: "", creditHospitalId: "" }));
                          }}
                          renderInput={(params) => <TextField {...params} label="Hospital *" fullWidth />}
                        />
                      )}
                    </Box>
                  )}
                  {!deltaPopup.asCredit && modes.map((m) => (
                    <Box key={m} display="flex" gap={1}>
                      <TextField
                        size="small" label={`${m.toUpperCase()} ${isRefund ? "Refund" : "Amount"}`} sx={{ flex: 1 }}
                        inputProps={{ inputMode: "decimal", maxLength: 12 }}
                        value={deltaPopup.form?.[`${m}_amount`] ?? ""}
                        onChange={handleAmount(m)}
                      />
                      {m !== "cash" && (
                        <TextField
                          size="small" label={`${m.toUpperCase()} Ref #`} sx={{ flex: 1 }}
                          inputProps={{ maxLength: 50 }}
                          value={deltaPopup.form?.[`${m}_transaction_no`] ?? ""}
                          onChange={(e) => setDeltaPopup((p) => ({ ...p, form: { ...p.form, [`${m}_transaction_no`]: e.target.value } }))}
                        />
                      )}
                    </Box>
                  ))}
                </>
              );
            })()}
            {deltaPopup.mode === "noop" && (
              <Typography sx={{ fontSize: "0.9vw", fontWeight: 600, color: "#666", mt: 1 }}>
                No money movement required — only line items and totals will be updated.
              </Typography>
            )}
            <TextField
              label="Reason (optional)"
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              value={deltaPopup.reason ?? ""}
              onChange={(e) => setDeltaPopup((p) => ({ ...p, reason: e.target.value }))}
              inputProps={{ maxLength: 500 }}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: "column", alignItems: "stretch" }}>
          {(() => {
            if (deltaPopup.mode === "extra" && deltaPopup.asCredit) {
              if (!useRefferalAsCredit && deltaPopup.creditType === "corporate" && !String(deltaPopup.creditHospitalName || "").trim()) {
                return (
                  <Typography variant="caption" color="error" sx={{ alignSelf: "flex-start", mb: 0.5 }}>
                    Hospital is required for Hospital Credit
                  </Typography>
                );
              }
              return null;
            }
            if (deltaPopup.mode === "noop") return null;
            if (!CommonValidation.isTransactionNumberMandatory()) return null;
            const f = deltaPopup.form || {};
            const missing = ["card", "upi", "bank", "cheque"].filter((m) =>
              (+f[`${m}_amount`] || 0) > 0 && !((f[`${m}_transaction_no`] || "").trim())
            );
            if (!missing.length) return null;
            return (
              <Typography variant="caption" color="error" sx={{ alignSelf: "flex-start", mb: 0.5 }}>
                Reference # is required for: {missing.map((m) => m.toUpperCase()).join(", ")}
              </Typography>
            );
          })()}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button size="small" onClick={() => setDeltaPopup((p) => ({ ...p, open: false }))}>Cancel</Button>
            <Button
              size="small"
              variant="contained"
              disabled={(() => {
                if (deltaPopup.mode === "noop") return false;
                if (deltaPopup.mode === "extra" && deltaPopup.asCredit) {
                  if (!useRefferalAsCredit && deltaPopup.creditType === "corporate" && !String(deltaPopup.creditHospitalName || "").trim()) {
                    return true;
                  }
                  return false;
                }
                const f = deltaPopup.form || {};
                const total = ["cash_amount", "card_amount", "upi_amount", "bank_amount", "cheque_amount"]
                  .reduce((s, k) => s + (+f[k] || 0), 0);
                const need = deltaPopup.mode === "extra" ? deltaPopup.extraNeeded : deltaPopup.refundNeeded;
                if (+total.toFixed(2) !== +Number(need || 0).toFixed(2)) return true;
                if (CommonValidation.isTransactionNumberMandatory()) {
                  const refMissing = ["card", "upi", "bank", "cheque"].some((m) =>
                    (+f[`${m}_amount`] || 0) > 0 && !((f[`${m}_transaction_no`] || "").trim())
                  );
                  if (refMissing) return true;
                }
                return false;
              })()}
              onClick={submitModifyBill}
            >
              Confirm
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Loader loaderOpen={isLoader} />
    </Box>
  );
}
